import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { useAppSelector } from 'hooks/useRedux';
import { RootState } from 'store';
import Button from 'components/Buttons/Button';
import Render from 'components/Common/Render';
import { Loader } from 'components/Common/Loader';
import MerchantPin from 'containers/MerchantPin';
import CustomerCardDetails from 'containers/PaymentMethodManagement/CustomerCradDetails';
import PayoutMethods from 'components/PayoutManagement/PayoutMethods';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import EditCardForm from 'containers/PaymentMethodManagement/EditCardForm';
import ModalComponent from 'components/Common/Modal';
import { useLazyDefaultAccountQuery } from 'store/apis/defaultAccountApi';
import { useAppDispatch } from 'hooks/useRedux';
import { setDefaultAccount } from 'store/slices/defaultAccountSlice';
import { useAuth } from 'hooks/useAuth';
import PhoneInputWithFlag, { CountryOption } from 'components/PayoutManagement/PhoneInputWithFlag';
import ToggleButton from 'components/FormFields/ToggleButton';
import { SelectField } from 'components/FormFields';
import { PiEye, PiTrash } from 'react-icons/pi';
import { IoMdClose } from 'react-icons/io';
import { useCategoriesQuery } from 'store/apis/categoryApi';
import { useExternalLinkTypesQuery } from 'store/apis/externalLinksApi';
import { TrashIcon, XIcon } from 'components/Common/Icons';
import { useLazyBusinessQuery } from 'store/apis/businessApi';
import { useLazyCountryListQuery } from 'store/apis/countryListApi';
import { useLazyStateListQuery } from 'store/apis/stateListApi';
import { useLazyCityListQuery } from 'store/apis/cityListApi';
import { toast } from 'react-hot-toast';
import { http } from 'middleware/apiMiddleware';
import { updateBusiness, getUser } from 'constants/apiUrls';
import axios from 'axios';
import { useGetPreSignedUrlMutation } from 'store/apis/imageUploadApi';
import {
  ImageStatus,
  PhotoSource,
  BusinessEditPhotoProps,
} from 'containers/OnboardingBusiness/types';
import {
  useGetBusinessUsersQuery,
  useGetInvitedUsersQuery,
  useInviteUserMutation,
  useCancelInvitationMutation,
} from 'store/apis/businessUsersApi';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_API_KEY || '');

const MAX_BANNER_IMAGE_SIZE = 20971520; // 20 MB in bytes
const MAX_BANNER_IMAGE_COUNT = 5;
const PHOTO_NUMBER_BADGE_CLASS =
  'w-5 h-5 rounded-full bg-white text-black flex items-center justify-center font-semibold text-[10px]';

// Social Media Platform Icons (fallback SVG paths)
const socialMediaIcons: { [key: string]: string } = {
  amazon:
    'M14.538 18.455c-2.667 1.971-6.538 3.018-9.87 3.018-4.672 0-8.876-1.729-12.057-4.608-.25-.225-.026-.532.273-.357 3.428 1.995 7.667 3.195 12.043 3.195 2.953 0 6.201-.613 9.191-1.881.45-.191.827.295.42.633m1.097-1.252c-.341-.438-2.257-.207-3.116-.104-.262.031-.302-.196-.066-.361 1.527-1.074 4.032-.764 4.324-.404.293.363-.076 2.877-1.513 4.079-.221.185-.431.087-.333-.158.323-.806 1.045-2.614.704-3.052',
  facebook:
    'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z',
  'google maps':
    'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
  instagram:
    'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z',
  linkedin:
    'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z',
  pinterest:
    'M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.758-1.379l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.55.535 6.607 0 11.985-5.365 11.985-11.987C23.97 5.39 18.592.026 11.985.026L12.017 0z',
  'trip advisor':
    'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm8 0c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z',
  twitter:
    'M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z',
  yelp: 'M12.271 17.718c-.07.18-.241.29-.421.29h-4.32c-.24 0-.42-.19-.42-.43v-9.5c0-.24.18-.43.42-.43h4.32c.18 0 .351.11.421.29l2.279 6.01c.12.32-.05.67-.38.78l-1.899.71c-.33.12-.67-.05-.78-.38l-2.279-6.01z M18.5 8.5c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zm-1.5 7c0-1.1.9-2 2-2s2 .9 2 2-.9 2-2 2-2-.9-2-2z',
  youtube:
    'M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z',
  other:
    'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z',
};

type TabType =
  | 'profile-info'
  | 'basic-info'
  | 'additional-info'
  | 'business-photos'
  | 'pin-management'
  | 'payment-management'
  | 'user-management';

type UserManagementSubTab = 'users' | 'invitations';

type PhotoOrderItem = {
  type: 'existing' | 'new';
  uuid: string;
};

type TimeSlot = { startTime: string; endTime: string };
type BusinessHoursState = Record<string, boolean>;
type BusinessHoursTimesState = Record<string, TimeSlot[]>;

const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const initialBusinessHours: BusinessHoursState = {
  monday: false,
  tuesday: false,
  wednesday: false,
  thursday: false,
  friday: false,
  saturday: false,
  sunday: false,
};

const initialBusinessHoursTimes: BusinessHoursTimesState = {
  monday: [{ startTime: '', endTime: '' }],
  tuesday: [{ startTime: '', endTime: '' }],
  wednesday: [{ startTime: '', endTime: '' }],
  thursday: [{ startTime: '', endTime: '' }],
  friday: [{ startTime: '', endTime: '' }],
  saturday: [{ startTime: '', endTime: '' }],
  sunday: [{ startTime: '', endTime: '' }],
};

const tabs: { id: TabType; label: string }[] = [
  { id: 'profile-info', label: 'Profile Info' },
  { id: 'basic-info', label: 'Basic Info' },
  { id: 'additional-info', label: 'Additional Information' },
  { id: 'business-photos', label: 'Business Photos' },
  { id: 'pin-management', label: 'Merchant App PIN' },
  { id: 'payment-management', label: 'Payment Management' },
  { id: 'user-management', label: 'User Management' },
];

// Extended business type with additional fields from API
type ExtendedBusinessType = BusinessType & {
  website?: string;
  phoneNumber?: string[];
  // API returns openExternalLinksInfo with nested info object
  openExternalLinksInfo?: {
    uuid: string;
    url: string;
    name?: string;
    info?: {
      metaExternalLinkUuid: string;
      name: string;
      logo: string;
      isOther: boolean;
    };
  }[];
  // API returns businessOpeningHoursInfo (not businessOpeningHours)
  businessOpeningHoursInfo?: { day: number; startTime: string; endTime: string }[];
  businessHourNotes?: string;
  // API may return photos under different field names
  photos?: { uuid: string; photoUrl: string }[] | string[];
  photosInfo?: { uuid: string; photoUrl: string; url?: string }[];
  businessPhotosInfo?: { uuid: string; photoUrl: string; url?: string }[];
};

const asString = (value: unknown): string => (typeof value === 'string' ? value : '');

type ApiRecord = Record<string, unknown>;

type ApiErrorResponse = {
  data?: {
    email?: string | string[];
    message?: string;
  };
};

const BusinessDetails = () => {
  const dispatch = useAppDispatch();
  const [selectedBusiness] = useLocalStorage('selectedBusiness', null);
  const businessState = useAppSelector((state: RootState) => state.businesses);
  const storedBusiness: ExtendedBusinessType = businessState?.business || selectedBusiness || null;

  // Fetch complete business data from API
  const [fetchBusiness, { data: fetchedBusinessData, isLoading: isLoadingBusiness }] =
    useLazyBusinessQuery();

  // Payment management state
  const [
    fetchDefaultAccount,
    {
      data: defaultPaymentMethod,
      isFetching: _isFetchingCustomerDetails,
      isSuccess: isDefaultAccountSuccess,
    },
  ] = useLazyDefaultAccountQuery({});
  const [clientSecret, setClientSecret] = useState('');
  const [isEditCard, setIsEditCard] = useState(false);
  const [isCreatingIntent, setIsCreatingIntent] = useState(false);

  // Use fetched data if available, otherwise use stored business
  const business: ExtendedBusinessType =
    (fetchedBusinessData as ExtendedBusinessType) || storedBusiness;

  const [activeTab, setActiveTab] = useState<TabType | null>('profile-info');

  // Profile Info state
  const authContext = useAuth();
  const [profileEditMode, setProfileEditMode] = useState(false);
  const [profileName, setProfileName] = useState('');
  const [profilePhone, setProfilePhone] = useState('');
  const [profilePhoneCountryCode, setProfilePhoneCountryCode] = useState('US');
  const [profilePhoneCountryExtension, setProfilePhoneCountryExtension] = useState('+1');
  const [profilePhoneCountryName, setProfilePhoneCountryName] = useState('United States');
  const [profilePhoto, setProfilePhoto] = useState('');
  const [profilePhotoFile, setProfilePhotoFile] = useState<File | null>(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState('');
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const profileFileInputRef = useRef<HTMLInputElement>(null);

  // Sync profile state from auth context
  useEffect(() => {
    if (authContext?.user) {
      const user = authContext.user as ApiRecord;
      setProfileName(asString(user.name));
      setProfilePhone(
        asString(user.phoneNumber) || asString(user.phone_number) || asString(user.phone),
      );
      let photo = asString(user.profilePicture) || asString(user.profile_picture);
      if (photo && !photo.includes('t=')) {
        const separator = photo.includes('?') ? '&' : '?';
        photo = `${photo}${separator}t=${Date.now()}`;
      }
      setProfilePhoto(photo);
      const cc = asString(user.phoneCountryCode) || asString(user.phone_country_code);
      const ce = asString(user.phoneCountryExtension) || asString(user.phone_country_extension);
      const cn = asString(user.phoneCountryName) || asString(user.phone_country_name);
      if (cc) setProfilePhoneCountryCode(cc);
      if (ce) setProfilePhoneCountryExtension(ce);
      if (cn) setProfilePhoneCountryName(cn);
    }
  }, [authContext?.user]);

  // Fetch fresh user data from API when profile-info tab is active
  useEffect(() => {
    if (activeTab === 'profile-info' && authContext?.user?.uuid) {
      const fetchLatestUserData = async () => {
        const { data } = await http.get(getUser(authContext.user!.uuid));
        if (data) {
          const raw = data as ApiRecord;
          const userData: ApiRecord = { ...raw };
          userData.phoneNumber = asString(raw.phoneNumber) || asString(raw.phone_number);
          userData.phoneCountryCode =
            asString(raw.phoneCountryCode) || asString(raw.phone_country_code);
          userData.phoneCountryExtension =
            asString(raw.phoneCountryExtension) || asString(raw.phone_country_extension);
          userData.phoneCountryName =
            asString(raw.phoneCountryName) || asString(raw.phone_country_name);
          userData.profilePicture = asString(raw.profilePicture) || asString(raw.profile_picture);
          if (userData.profilePicture) {
            userData.profilePicture = `${asString(userData.profilePicture)}?t=${Date.now()}`;
          }
          authContext.updateUser?.(userData);
        }
      };
      fetchLatestUserData();
    }
  }, [activeTab]);

  // Refetch business users when User Management tab is active to get latest avatars
  useEffect(() => {
    if (activeTab === 'user-management' && storedBusiness?.uuid) {
      refetchBusinessUsers();
    }
  }, [activeTab]);

  // Fetch categories and external link types from API
  const { data: categoriesData, isLoading: isLoadingCategories } = useCategoriesQuery();
  const { data: externalLinkTypes, isLoading: isLoadingExternalLinks } =
    useExternalLinkTypesQuery();

  // Fetch complete business data when component mounts
  useEffect(() => {
    if (storedBusiness?.uuid) {
      fetchBusiness({ uuid: storedBusiness.uuid });
    }
  }, [storedBusiness?.uuid, fetchBusiness]);

  const [isUpdating, setIsUpdating] = useState(false);

  const isEditing = true;

  // Country, State, City APIs
  const [fetchCountries, { data: countries, isLoading: isLoadingCountries }] =
    useLazyCountryListQuery();
  const [fetchStates, { data: states, isLoading: isLoadingStates }] = useLazyStateListQuery();
  const [fetchCities, { data: cities, isLoading: isLoadingCities }] = useLazyCityListQuery();

  // Basic Info state
  const [businessName, setBusinessName] = useState('');
  const [streetAddress, setStreetAddress] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<{ name: string; value: string } | null>(
    null,
  );
  const [selectedState, setSelectedState] = useState<{ name: string; value: string } | null>(null);
  const [selectedCity, setSelectedCity] = useState<{ name: string; value: string } | null>(null);
  const [zipCode, setZipCode] = useState('');

  // Additional Info state
  const [category, setCategory] = useState<{ name: string; value: string } | null>(null);
  const [website, setWebsite] = useState('');
  const [businessPhones, setBusinessPhones] = useState<string[]>(['']);
  const [socialLinks, setSocialLinks] = useState<{ platform: string; url: string }[]>([
    { platform: '', url: '' },
  ]);
  const [businessHours, setBusinessHours] = useState<BusinessHoursState>(initialBusinessHours);
  const [businessHoursTimes, setBusinessHoursTimes] =
    useState<BusinessHoursTimesState>(initialBusinessHoursTimes);
  const [businessHoursNote, setBusinessHoursNote] = useState('');

  // Validation errors state
  const [validationErrors, setValidationErrors] = useState<{
    website?: string;
    phones?: string[];
    socialLinks?: string[];
  }>({});

  // Business Photos state - include uuid for deletion tracking
  const [businessPhotos, setBusinessPhotos] = useState<{ id: number; url: string; uuid: string }[]>(
    [],
  );
  const [deletedPhotoUuids, setDeletedPhotoUuids] = useState<string[]>([]);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<{
    id: number;
    url: string;
    uuid: string;
  } | null>(null);

  // Photo upload state
  const [newPhotos, setNewPhotos] = useState<BusinessEditPhotoProps[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [getPreSignedUrl] = useGetPreSignedUrlMutation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Photo reorder drag state
  const [draggedPhotoIndex, setDraggedPhotoIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  // Track combined photo order for drag-and-drop reordering
  const [photoOrder, setPhotoOrder] = useState<PhotoOrderItem[]>([]);

  // Mobile detection and modal state for social links
  const [isMobile, setIsMobile] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newLink, setNewLink] = useState({ platform: '', url: '' });

  // Expanded days state for mobile collapsible business hours
  const [_expandedDays, setExpandedDays] = useState<Record<string, boolean>>({});

  // User Management state
  const [userManagementSubTab, setUserManagementSubTab] = useState<UserManagementSubTab>('users');

  // User Management API calls
  const {
    data: businessUsersData,
    isLoading: isLoadingUsers,
    refetch: refetchBusinessUsers,
  } = useGetBusinessUsersQuery(
    { businessUuid: storedBusiness?.uuid || '' },
    { skip: !storedBusiness?.uuid },
  );

  const {
    data: invitedUsersData,
    isLoading: isLoadingInvitations,
    refetch: refetchInvitations,
  } = useGetInvitedUsersQuery(
    { businessUuid: storedBusiness?.uuid || '' },
    { skip: !storedBusiness?.uuid },
  );

  const [inviteUser, { isLoading: isInviting }] = useInviteUserMutation();
  const [cancelInvitation] = useCancelInvitationMutation();

  // Transform API data to component format
  const businessUsers = useMemo(() => {
    if (!businessUsersData?.results) return [];
    return businessUsersData.results.map((user) => {
      const raw = user.user as ApiRecord | undefined;
      let avatarUrl: string | undefined =
        asString(raw?.profilePicture) || asString(raw?.profile_picture) || undefined;
      if (avatarUrl) {
        const sep = avatarUrl.includes('?') ? '&' : '?';
        avatarUrl = `${avatarUrl}${sep}t=${Date.now()}`;
      }
      return {
        uuid: user.uuid,
        name: asString(raw?.name) || user.formattedName || '',
        email: asString(raw?.email),
        avatar: avatarUrl,
        role: user.role,
      };
    });
  }, [businessUsersData]);

  const invitations = useMemo(() => {
    if (!invitedUsersData?.results) return [];
    return invitedUsersData.results.map((inv) => ({
      uuid: inv.uuid || inv.id || inv.invitationId || inv.invitation_id || inv.email || '',
      email: inv.email,
      status: (inv.status || 'pending') as 'pending' | 'accepted' | 'expired',
      sentAt: inv.createdAt || inv.invitedAt || inv.created_at || inv.invited_at || '',
    }));
  }, [invitedUsersData]);

  const [invitationMenuOpen, setInvitationMenuOpen] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [showResendModal, setShowResendModal] = useState(false);
  const [resendEmail, setResendEmail] = useState('');

  // Detect if mobile view and set initial tab
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // On desktop, auto-select first tab if no tab is selected
      if (!mobile && activeTab === null) {
        setActiveTab('basic-info');
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [activeTab]);

  // Payment management handlers
  const fetchClientSecret = async () => {
    try {
      setIsCreatingIntent(true);
      const { data, error } = await http.get(`/business/${storedBusiness?.uuid}/card-setup-intent`);
      if (data && !error) {
        setClientSecret(data.setupIntentClientSecret);
      }
    } catch {
      // Error creating intent
    } finally {
      setIsCreatingIntent(false);
    }
  };

  const onEditCard = () => {
    setClientSecret('');
    setIsEditCard(true);
    fetchClientSecret();
  };

  const onCancelEditCard = () => {
    setIsEditCard(false);
    setClientSecret('');
  };

  const onCardUpdateSuccess = () => {
    setIsEditCard(false);
    setClientSecret('');
    if (storedBusiness?.uuid) {
      fetchDefaultAccount({ uuid: storedBusiness.uuid });
    }
  };

  const [isPaymentDataReady, setIsPaymentDataReady] = useState(false);

  // Fetch default payment method when payment tab is active
  useEffect(() => {
    if (activeTab === 'payment-management' && storedBusiness?.uuid) {
      setIsPaymentDataReady(false);
      fetchDefaultAccount({ uuid: storedBusiness.uuid }).finally(() => {
        setIsPaymentDataReady(true);
      });
    } else {
      setIsPaymentDataReady(false);
    }
  }, [activeTab, storedBusiness?.uuid]);

  // Update default account in redux when fetched
  useEffect(() => {
    if (defaultPaymentMethod && isDefaultAccountSuccess) {
      dispatch(setDefaultAccount(defaultPaymentMethod));
    }
  }, [defaultPaymentMethod, isDefaultAccountSuccess, dispatch]);

  // Transform categories data for SelectField
  const categoryOptions = useMemo(() => {
    if (!categoriesData) return [];
    return categoriesData.map((cat) => ({ name: cat.name, value: cat.uuid }));
  }, [categoriesData]);

  // Transform external link types for platform options (filtered list like AdditionalInformationForm)
  const platformOptions = useMemo(() => {
    if (!externalLinkTypes || externalLinkTypes.length === 0) {
      return [];
    }

    // Allowed platforms list (excluding Web and Whatsapp)
    const allowedPlatforms = [
      'amazon',
      'facebook',
      'google maps',
      'instagram',
      'linkedin',
      'pinterest',
      'trip advisor',
      'twitter',
      'yelp',
      'youtube',
      'other',
    ];

    return externalLinkTypes
      .filter((type) => allowedPlatforms.includes(type.name.toLowerCase()))
      .map((type) => ({
        value: type.name.toLowerCase(),
        label: type.name,
        uuid: type.metaExternalLinkUuid,
        logo: type.logo,
        isOther: type.isOther,
      }));
  }, [externalLinkTypes]);

  // Get available platform options for a specific index (excludes already used platforms)
  const getAvailablePlatformOptions = (currentIndex: number) => {
    const usedPlatforms = socialLinks
      .filter((_, index) => index !== currentIndex) // Exclude current row
      .map((link) => link.platform.toLowerCase())
      .filter((platform) => platform); // Filter out empty platforms

    return platformOptions.filter((option) => !usedPlatforms.includes(option.value.toLowerCase()));
  };

  // Check if a platform is already used (for validation)
  const isPlatformAlreadyUsed = (platform: string, excludeIndex?: number) => {
    return socialLinks.some(
      (link, index) =>
        index !== excludeIndex && link.platform.toLowerCase() === platform.toLowerCase(),
    );
  };

  // Get platform icon component (uses API logo or fallback SVG)
  const getPlatformIcon = (platform: string) => {
    // First try to get logo from API
    const platformOption = platformOptions.find((opt) => opt.value === platform.toLowerCase());

    if (platformOption && platformOption.logo) {
      return (
        <img
          src={platformOption.logo}
          alt={platformOption.label}
          className="w-5 h-5 object-contain"
          onError={(e) => {
            // If image fails to load, hide it
            e.currentTarget.style.display = 'none';
          }}
        />
      );
    }

    // Fallback to SVG icon
    const iconPath = socialMediaIcons[platform.toLowerCase()];
    if (!iconPath) return null;

    return (
      <svg
        className={`w-5 h-5 ${platform === 'facebook' ? 'text-facebookBlue' : 'text-lightGrey'}`}
        viewBox="0 0 24 24"
        fill="currentColor"
      >
        <path d={iconPath} />
      </svg>
    );
  };

  // Map day number to day name
  const dayNumberToName: Record<number, string> = {
    1: 'monday',
    2: 'tuesday',
    3: 'wednesday',
    4: 'thursday',
    5: 'friday',
    6: 'saturday',
    7: 'sunday',
  };

  // Fetch countries on mount
  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  // Fetch states when country changes
  useEffect(() => {
    if (selectedCountry?.value) {
      fetchStates({ countryCode: selectedCountry.value });
    }
  }, [selectedCountry, fetchStates]);

  // Fetch cities when state changes
  useEffect(() => {
    if (selectedCountry?.value && selectedState?.value) {
      fetchCities({
        countryCode: selectedCountry.value,
        stateCode: selectedState.value,
      });
    }
  }, [selectedCountry, selectedState, fetchCities]);

  // Transform country options for SelectField
  const countryOptions = useMemo(() => {
    if (!countries?.result?.length) return [];
    return countries.result.map((c: { name: string; isoCode: string }) => ({
      name: c.name,
      value: c.isoCode,
    }));
  }, [countries]);

  // Transform state options for SelectField
  const stateOptions = useMemo(() => {
    if (!states?.result?.length) return [];
    return states.result.map((s: { name: string; isoCode: string }) => ({
      name: s.name,
      value: s.isoCode,
    }));
  }, [states]);

  // Transform city options for SelectField
  const cityOptions = useMemo(() => {
    if (!cities?.result?.length) return [];
    return cities.result.map((c: { name: string }) => ({ name: c.name, value: c.name }));
  }, [cities]);

  // Initialize state from business data when available
  useEffect(() => {
    if (business) {
      setBusinessName(business.name || '');
      setStreetAddress(business.address?.address1 || '');

      if (business.address?.country && business.address?.countryShortCode) {
        setSelectedCountry({
          name: business.address.country,
          value: business.address.countryShortCode,
        });
      }
      if (business.address?.state && business.address?.stateShortCode) {
        setSelectedState({
          name: business.address.state,
          value: business.address.stateShortCode,
        });
      }
      if (business.address?.city) {
        setSelectedCity({
          name: business.address.city,
          value: business.address.city,
        });
      }
      setZipCode(business.address?.zipCode || '');

      if (business.categories && business.categories.length > 0) {
        const firstCategory = business.categories[0];
        setCategory({ name: firstCategory.name, value: firstCategory.uuid });
      }

      if (business.website) {
        setWebsite(business.website);
      }

      // Set phone numbers (API returns phoneNumber, not phoneNumbers)
      if (business.phoneNumber && business.phoneNumber.length > 0) {
        setBusinessPhones(business.phoneNumber);
      }

      // Set external links (social links) - API uses openExternalLinksInfo
      if (business.openExternalLinksInfo && business.openExternalLinksInfo.length > 0) {
        // Map API response to component format
        const mappedLinks = business.openExternalLinksInfo.map((link) => ({
          platform: link.info?.name?.toLowerCase() || 'other',
          url: link.url,
        }));
        setSocialLinks(mappedLinks);
      }

      // Set business hours - API uses businessOpeningHoursInfo
      if (business.businessOpeningHoursInfo && business.businessOpeningHoursInfo.length > 0) {
        const hoursState: BusinessHoursState = { ...initialBusinessHours };
        const timesState: BusinessHoursTimesState = { ...initialBusinessHoursTimes };

        business.businessOpeningHoursInfo.forEach((hour) => {
          // Day is numeric (1 = Monday, 2 = Tuesday, etc.)
          const dayKey = dayNumberToName[hour.day] || '';

          if (dayKey) {
            hoursState[dayKey] = true;

            // Convert 12-hour format (04:13PM) to 24-hour format (16:13)
            const convertTo24Hour = (time12: string): string => {
              if (!time12) return '';
              const match = time12.match(/^(\d{1,2}):(\d{2})(AM|PM)$/i);
              if (!match) return time12; // Return as-is if format doesn't match

              let hours = parseInt(match[1], 10);
              const minutes = match[2];
              const period = match[3].toUpperCase();

              if (period === 'PM' && hours !== 12) {
                hours += 12;
              } else if (period === 'AM' && hours === 12) {
                hours = 0;
              }

              return `${hours.toString().padStart(2, '0')}:${minutes}`;
            };

            const startTime24 = convertTo24Hour(hour.startTime);
            const endTime24 = convertTo24Hour(hour.endTime);

            // Find existing slot or add new one
            const existingSlots = timesState[dayKey] || [];
            const hasEmptySlot = existingSlots.some((s) => !s.startTime && !s.endTime);

            if (hasEmptySlot) {
              timesState[dayKey] = [{ startTime: startTime24, endTime: endTime24 }];
            } else {
              timesState[dayKey] = [
                ...existingSlots,
                { startTime: startTime24, endTime: endTime24 },
              ];
            }
          }
        });

        setBusinessHours(hoursState);
        setBusinessHoursTimes(timesState);
      }

      if (business.businessHourNotes) {
        setBusinessHoursNote(business.businessHourNotes);
      }

      // Set photos - API may return photos under different field names
      // Check for photos, photosInfo, or businessPhotosInfo
      const photosData =
        business.photos || business.photosInfo || business.businessPhotosInfo || [];

      if (photosData && photosData.length > 0) {
        const mappedPhotos = photosData.map(
          (
            photo:
              | string
              | { uuid?: string; id?: string; photoUrl?: string; url?: string; imageUrl?: string },
            index: number,
          ) => {
            if (typeof photo === 'string') {
              return { id: index + 1, url: photo, uuid: '' };
            } else {
              // Handle object with photoUrl, url, or other URL field
              const photoUrl =
                asString(photo.photoUrl) || asString(photo.url) || asString(photo.imageUrl);
              const photoUuid = asString(photo.uuid) || asString(photo.id);
              return { id: index + 1, url: photoUrl, uuid: photoUuid };
            }
          },
        );
        setBusinessPhotos(mappedPhotos);
        // Initialize photo order with existing photos
        setPhotoOrder(mappedPhotos.map((p) => ({ type: 'existing' as const, uuid: p.uuid })));
        // Reset deleted photos when fresh data is loaded
        setDeletedPhotoUuids([]);
        setNewPhotos([]);
      } else {
        setBusinessPhotos([]);
        setPhotoOrder([]);
        setDeletedPhotoUuids([]);
        setNewPhotos([]);
      }
    }
  }, [business]);

  const isLoading = isLoadingCategories || isLoadingExternalLinks || isLoadingBusiness;

  // Validate all fields before save
  const validateAllFields = (): boolean => {
    const errors: {
      website?: string;
      phones?: string[];
      socialLinks?: string[];
    } = {};

    let hasErrors = false;

    // Validate website
    if (website) {
      const websiteError = validateWebsite(website);
      if (websiteError) {
        errors.website = websiteError;
        hasErrors = true;
      }
    }

    // Validate phone numbers
    const phoneErrors: string[] = [];
    businessPhones.forEach((phone, index) => {
      if (phone && !/^[0-9+\-() ]+$/.test(phone)) {
        phoneErrors[index] = 'Phone number can only contain numbers and +, -, (, ), space';
        hasErrors = true;
      } else {
        phoneErrors[index] = '';
      }
    });
    if (phoneErrors.some((e) => e)) {
      errors.phones = phoneErrors;
    }

    // Validate social links
    const linkErrors: string[] = [];
    socialLinks.forEach((link, index) => {
      if (link.url) {
        const urlError = validateSocialLinkUrl(link.url, link.platform);
        if (urlError) {
          linkErrors[index] = urlError;
          hasErrors = true;
        }
      }
    });
    if (linkErrors.some((e) => e)) {
      errors.socialLinks = linkErrors;
    }

    setValidationErrors(errors);
    return !hasErrors;
  };

  const handleButtonClick = () => {
    if (!validateAllFields()) {
      toast.error('Please fix the validation errors before saving');
      return;
    }
    handleUpdateBusiness();
  };

  const handleUpdateBusiness = async () => {
    if (!business?.uuid) {
      toast.error('No business selected');
      return;
    }

    try {
      // Prepare business hours data
      const dayNameToNumber: { [key: string]: number } = {
        monday: 1,
        tuesday: 2,
        wednesday: 3,
        thursday: 4,
        friday: 5,
        saturday: 6,
        sunday: 7,
      };

      const businessOpeningHours = Object.keys(businessHours)
        .filter((day) => businessHours[day])
        .flatMap((day) => {
          const dayTimes = businessHoursTimes[day] || [];
          return dayTimes
            .filter((slot) => slot.startTime && slot.endTime)
            .map((slot) => ({
              day: dayNameToNumber[day],
              startTime: slot.startTime,
              endTime: slot.endTime,
            }));
        });

      // Map social links to API format
      const openExternalLinks = socialLinks
        .filter((link) => link && link.platform && link.url && link.url.trim())
        .map((link) => {
          const externalLinkType = externalLinkTypes?.find(
            (type) => type.name.toLowerCase() === link.platform.toLowerCase(),
          );
          return {
            metaExternalLinkUuid: externalLinkType?.metaExternalLinkUuid || '',
            url: link.url.trim(),
          };
        })
        .filter((link) => link.metaExternalLinkUuid);

      // Prepare photos data - include ALL photos in order (existing + new) for reordering
      // The order in uploadedPhotos array determines the position on the backend
      const allPhotosInOrder = getAllPhotos();
      const uploadedPhotoUuids = allPhotosInOrder
        .filter((p) => {
          if (p.type === 'existing') {
            // Include existing photos that have a valid uuid
            return p.uuid && p.uuid.trim() !== '';
          } else {
            // Include new photos that are successfully uploaded
            const newPhoto = newPhotos.find((np) => np.uuid === p.uuid);
            return (
              newPhoto &&
              newPhoto.status === ImageStatus.UPLOADED &&
              p.uuid &&
              !p.uuid.startsWith('temp-')
            );
          }
        })
        .map((p) => p.uuid);

      // Always send photosData if there are any photos or deletions (to preserve order)
      const hasPhotosChanges =
        deletedPhotoUuids.length > 0 || uploadedPhotoUuids.length > 0 || businessPhotos.length > 0;
      const photosDataPayload = hasPhotosChanges
        ? {
            photosData: {
              deletedPhotos: deletedPhotoUuids,
              uploadedPhotos: uploadedPhotoUuids,
            },
          }
        : {};

      const updatePayload = {
        name: businessName.trim(),
        address: {
          address1: streetAddress.trim(),
          address2: '',
          zipCode: zipCode.trim(),
          state: selectedState?.name || '',
          stateShortCode: selectedState?.value || '',
          country: selectedCountry?.name || '',
          countryShortCode: selectedCountry?.value || '',
          city: selectedCity?.name || '',
        },
        website: website.trim(),
        phoneNumber: businessPhones.filter((phone) => phone.trim()).map((phone) => phone.trim()),
        categories: category?.value ? [category.value] : [],
        openExternalLinks,
        businessOpeningHours,
        businessHourNotes: businessHoursNote.trim(),
        ...photosDataPayload,
      };

      setIsUpdating(true);

      const { error, data } = await http.patch(updateBusiness(business.uuid), updatePayload);

      if (!error && !!data) {
        toast.success('Business updated successfully!');
        // Reset deleted photos and new photos after successful update
        setDeletedPhotoUuids([]);
        setNewPhotos([]);
        // Refresh business data
        fetchBusiness({ uuid: business.uuid });
      } else {
        toast.error('Failed to update business. Please try again.');
      }
    } catch (error) {
      toast.error('Failed to update business. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  // Photo handlers
  const handlePreviewPhoto = (photo: { id: number; url: string; uuid: string }) => {
    setPreviewImageUrl(photo.url);
    setShowImagePreview(true);
  };

  const handleDeletePhoto = (photo: { id: number; url: string; uuid: string }) => {
    setImageToDelete(photo);
    setShowDeleteConfirm(true);
  };

  const confirmDeletePhoto = () => {
    if (imageToDelete) {
      // Track the uuid of deleted photo for backend sync
      if (imageToDelete.uuid) {
        setDeletedPhotoUuids((prev) => [...prev, imageToDelete.uuid]);
        // Remove from photoOrder
        setPhotoOrder((prev) => prev.filter((p) => p.uuid !== imageToDelete.uuid));
      }
      setBusinessPhotos(businessPhotos.filter((p) => p.id !== imageToDelete.id));
      setShowDeleteConfirm(false);
      setImageToDelete(null);
    }
  };

  const cancelDeletePhoto = () => {
    setShowDeleteConfirm(false);
    setImageToDelete(null);
  };

  // Photo upload handlers
  const getTotalPhotosCount = () => businessPhotos.length + newPhotos.length;
  const canAddMorePhotos = () => getTotalPhotosCount() < MAX_BANNER_IMAGE_COUNT;
  const remainingPhotoSlots = () => MAX_BANNER_IMAGE_COUNT - getTotalPhotosCount();

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (canAddMorePhotos()) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (!canAddMorePhotos()) {
      toast.error(`Maximum ${MAX_BANNER_IMAGE_COUNT} photos allowed`);
      return;
    }
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files);
    }
    // Reset input value to allow selecting same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileSelect = (files: FileList) => {
    const remainingSlots = remainingPhotoSlots();
    const filesToProcess = Array.from(files).slice(0, remainingSlots);

    if (files.length > remainingSlots) {
      toast.error(`Only ${remainingSlots} more photo${remainingSlots > 1 ? 's' : ''} can be added`);
    }

    filesToProcess.forEach((file) => {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select only image files');
        return;
      }
      if (file.size > MAX_BANNER_IMAGE_SIZE) {
        toast.error('Image size must be less than 20MB');
        return;
      }
      uploadToS3(file);
    });
  };

  const uploadToS3 = async (file: File) => {
    const tempUuid = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newPhoto: BusinessEditPhotoProps = {
      rawImage: file,
      photoUrl: URL.createObjectURL(file),
      status: ImageStatus.UPLOADING,
      uuid: tempUuid,
      source: PhotoSource.BUSINESS,
    };

    setNewPhotos((prev) => [...prev, newPhoto]);

    try {
      const data = await getPreSignedUrl({}).unwrap();
      if (data) {
        const { fields: paramsFromRequestUrl, url, uuid } = data;
        const requestFormData = new FormData();

        Object.keys(paramsFromRequestUrl).forEach((key: string) => {
          const requestParams = paramsFromRequestUrl[key as keyof typeof paramsFromRequestUrl];
          requestFormData.append(key, asString(requestParams));
        });
        requestFormData.append('file', file);

        const uploadedFileUrl = `${url}${paramsFromRequestUrl.key}`;
        const result = await axios.post(url, requestFormData, {
          headers: {
            Authorization: '',
            Accept: 'application/json',
            'Content-Type': 'multipart/form-data',
          },
        });

        if (result.status === 200 || result.status === 204) {
          setNewPhotos((prev) =>
            prev.map((p) =>
              p.uuid === tempUuid
                ? { ...p, photoUrl: uploadedFileUrl, status: ImageStatus.UPLOADED, uuid }
                : p,
            ),
          );
          // Add newly uploaded photo to the order
          setPhotoOrder((prev) => [...prev, { type: 'new' as const, uuid }]);
          toast.success('Photo uploaded successfully');
        } else {
          setNewPhotos((prev) =>
            prev.map((p) => (p.uuid === tempUuid ? { ...p, status: ImageStatus.FAILED } : p)),
          );
          toast.error('Failed to upload photo');
        }
      }
    } catch (error) {
      setNewPhotos((prev) =>
        prev.map((p) => (p.uuid === tempUuid ? { ...p, status: ImageStatus.FAILED } : p)),
      );
      toast.error('Failed to upload photo');
    }
  };

  const retryUpload = (photo: BusinessEditPhotoProps) => {
    if (photo.rawImage && photo.rawImage instanceof File) {
      // Remove the failed photo and retry
      setNewPhotos((prev) => prev.filter((p) => p.uuid !== photo.uuid));
      uploadToS3(photo.rawImage);
    }
  };

  const removeNewPhoto = (uuid: string) => {
    setNewPhotos((prev) => prev.filter((p) => p.uuid !== uuid));
    // Remove from photoOrder
    setPhotoOrder((prev) => prev.filter((p) => p.uuid !== uuid));
  };

  // Photo reorder handlers - combine all photos for unified reordering
  const getAllPhotos = () => {
    // Use photoOrder to maintain the correct order after drag-and-drop
    return photoOrder
      .map((orderItem) => {
        if (orderItem.type === 'existing') {
          const photo = businessPhotos.find((p) => p.uuid === orderItem.uuid);
          if (photo) {
            return {
              type: 'existing' as const,
              id: photo.id,
              url: photo.url,
              uuid: photo.uuid,
            };
          }
        } else {
          const photo = newPhotos.find((p) => p.uuid === orderItem.uuid);
          if (photo) {
            return {
              type: 'new' as const,
              id: 0,
              url: photo.photoUrl,
              uuid: photo.uuid,
              photo,
            };
          }
        }
        return null;
      })
      .filter((p): p is NonNullable<typeof p> => p !== null);
  };

  const handlePhotoDragStart = (e: React.DragEvent, index: number) => {
    setDraggedPhotoIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handlePhotoDragEnd = () => {
    setDraggedPhotoIndex(null);
    setDragOverIndex(null);
  };

  const handlePhotoDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedPhotoIndex !== null && draggedPhotoIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handlePhotoDragLeave = () => {
    setDragOverIndex(null);
  };

  const handlePhotoDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedPhotoIndex === null || draggedPhotoIndex === dropIndex) {
      setDraggedPhotoIndex(null);
      setDragOverIndex(null);
      return;
    }

    // Reorder the photoOrder array
    const newOrder = [...photoOrder];
    const [draggedItem] = newOrder.splice(draggedPhotoIndex, 1);
    newOrder.splice(dropIndex, 0, draggedItem);

    setPhotoOrder(newOrder);
    setDraggedPhotoIndex(null);
    setDragOverIndex(null);
  };

  // Mobile touch-based reorder handlers
  const photoRefs = useRef<(HTMLDivElement | null)[]>([]);
  const touchStartY = useRef<number>(0);
  const [isTouchDragging, setIsTouchDragging] = useState(false);

  const handleTouchStart = (e: React.TouchEvent, index: number) => {
    if (!isEditing) return;
    touchStartY.current = e.touches[0].clientY;
    setDraggedPhotoIndex(index);
    setIsTouchDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isTouchDragging || draggedPhotoIndex === null) return;

    const touchY = e.touches[0].clientY;

    // Find which photo element we're over
    const allPhotos = getAllPhotos();
    for (let i = 0; i < allPhotos.length; i++) {
      const el = photoRefs.current[i];
      if (el && i !== draggedPhotoIndex) {
        const rect = el.getBoundingClientRect();
        if (touchY >= rect.top && touchY <= rect.bottom) {
          setDragOverIndex(i);
          return;
        }
      }
    }
    setDragOverIndex(null);
  };

  const handleTouchEnd = () => {
    if (!isTouchDragging) return;

    if (
      draggedPhotoIndex !== null &&
      dragOverIndex !== null &&
      draggedPhotoIndex !== dragOverIndex
    ) {
      // Reorder the photoOrder array
      const newOrder = [...photoOrder];
      const [draggedItem] = newOrder.splice(draggedPhotoIndex, 1);
      newOrder.splice(dragOverIndex, 0, draggedItem);
      setPhotoOrder(newOrder);
    }

    setDraggedPhotoIndex(null);
    setDragOverIndex(null);
    setIsTouchDragging(false);
  };

  // Toggle business hour for a day
  const toggleBusinessHour = (day: string) => {
    // Save current scroll position to prevent auto-scroll
    const scrollContainer = document.getElementById('business-details-content');
    const scrollTop = scrollContainer?.scrollTop || 0;

    setBusinessHours((prev) => ({ ...prev, [day]: !prev[day] }));

    // Restore scroll position after state update
    requestAnimationFrame(() => {
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollTop;
      }
    });
  };

  const _toggleExpandedDay = (day: string) => {
    setExpandedDays((prev) => ({ ...prev, [day]: !prev[day] }));
  };

  const handleTimeChange = (
    day: string,
    slotIndex: number,
    field: 'startTime' | 'endTime',
    value: string,
  ) => {
    setBusinessHoursTimes((prev) => {
      const updated = { ...prev };
      updated[day] = [...prev[day]];
      updated[day][slotIndex] = { ...updated[day][slotIndex], [field]: value };
      return updated;
    });
  };

  const addTimeSlot = (day: string) => {
    setBusinessHoursTimes((prev) => {
      if (prev[day].length < 2) {
        return { ...prev, [day]: [...prev[day], { startTime: '', endTime: '' }] };
      }
      return prev;
    });
  };

  const removeTimeSlot = (day: string, slotIndex: number) => {
    setBusinessHoursTimes((prev) => {
      const updated = { ...prev };
      updated[day] = prev[day].filter((_, index) => index !== slotIndex);
      if (updated[day].length === 0) {
        updated[day] = [{ startTime: '', endTime: '' }];
      }
      return updated;
    });
  };

  const addBusinessPhone = () => {
    setBusinessPhones([...businessPhones, '']);
  };

  const removeBusinessPhone = (index: number) => {
    if (businessPhones.length > 1) {
      setBusinessPhones(businessPhones.filter((_, i) => i !== index));
    }
  };

  // Handle business phone change - only allow numeric and phone characters
  const handleBusinessPhoneChange = (index: number, value: string) => {
    // Only allow numbers, +, -, (, ), and spaces
    const sanitizedValue = value.replace(/[^0-9+\-() ]/g, '');
    const updated = [...businessPhones];
    updated[index] = sanitizedValue;
    setBusinessPhones(updated);

    // Clear phone error for this index if value is valid
    if (sanitizedValue) {
      const phoneErrors = [...(validationErrors.phones || [])];
      phoneErrors[index] = '';
      setValidationErrors({ ...validationErrors, phones: phoneErrors });
    }
  };

  const validateWebsite = (url: string): string => {
    if (!url) return '';
    const urlPattern = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/i;
    if (!urlPattern.test(url)) {
      return 'Please enter a valid website URL';
    }
    return '';
  };

  const handleWebsiteChange = (value: string) => {
    setWebsite(value);
    const error = validateWebsite(value);
    setValidationErrors({ ...validationErrors, website: error });
  };

  const validateSocialLinkUrl = (url: string, platform: string): string => {
    if (!url) return '';

    // For username-based platforms, allow @username format
    if (platform === 'instagram' || platform === 'twitter') {
      if (url.startsWith('@') || url.startsWith('http')) {
        return '';
      }
      // Also allow plain usernames
      return '';
    }

    // For URL-based platforms, validate URL format
    const urlPattern = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/i;
    if (!urlPattern.test(url)) {
      return 'Please enter a valid URL';
    }
    return '';
  };

  const addSocialLinkInline = () => {
    setSocialLinks([...socialLinks, { platform: '', url: '' }]);
  };

  const removeSocialLink = (index: number) => {
    setSocialLinks(socialLinks.filter((_, i) => i !== index));
  };

  const handleSocialLinkChange = (index: number, field: 'platform' | 'url', value: string) => {
    // Check for duplicate platform when changing platform field
    if (field === 'platform' && value && isPlatformAlreadyUsed(value, index)) {
      toast.error('This platform is already added. Please select a different one.');
      return;
    }

    const updated = [...socialLinks];
    updated[index][field] = value;
    setSocialLinks(updated);

    // Validate URL if that's what changed
    if (field === 'url') {
      const linkErrors = [...(validationErrors.socialLinks || [])];
      linkErrors[index] = validateSocialLinkUrl(value, updated[index].platform);
      setValidationErrors({ ...validationErrors, socialLinks: linkErrors });
    }
  };

  const openAddLinkModal = () => {
    setNewLink({ platform: '', url: '' });
    setIsModalOpen(true);
  };

  const handleAddLink = () => {
    if (!newLink.url.trim()) {
      toast.error('Please enter a URL');
      return;
    }

    // Check for duplicate platform
    if (isPlatformAlreadyUsed(newLink.platform)) {
      toast.error('This platform is already added. Please select a different one.');
      return;
    }

    setSocialLinks([...socialLinks, { ...newLink }]);
    setIsModalOpen(false);
    setNewLink({ platform: '', url: '' });
  };

  const getTabLabel = (tabId: TabType | null): string => {
    const tab = tabs.find((t) => t.id === tabId);
    return tab?.label || '';
  };

  const handleMobileBack = () => {
    setActiveTab(null);
  };

  const handleTabSelect = (tabId: TabType) => {
    setActiveTab(tabId);
    if (tabId !== 'profile-info') {
      setProfileEditMode(false);
    }
  };

  const handleProfilePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }
    if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
      toast.error('Only JPG or PNG files are allowed');
      return;
    }
    setProfilePhotoFile(file);
    setProfilePhotoPreview(URL.createObjectURL(file));
  };

  const handleRemoveProfilePhoto = () => {
    setProfilePhotoFile(null);
    setProfilePhotoPreview('');
    setProfilePhoto('');
  };

  const handleProfileSave = async () => {
    if (!authContext?.user?.uuid) return;
    try {
      setIsUpdatingProfile(true);

      const formData = new FormData();
      formData.append('name', profileName.trim());
      const phoneToSend = profilePhone.startsWith('+')
        ? profilePhone
        : profilePhone
          ? `${profilePhoneCountryExtension}${profilePhone}`
          : '';
      formData.append('phoneNumber', phoneToSend);
      formData.append('phoneCountryCode', profilePhoneCountryCode);
      formData.append('phoneCountryExtension', profilePhoneCountryExtension);
      formData.append('phoneCountryName', profilePhoneCountryName);

      if (profilePhotoFile) {
        formData.append('profilePicture', profilePhotoFile);
      } else if (!profilePhoto && !profilePhotoPreview) {
        formData.append('profilePicture', '');
      }

      const { data: responseData, error } = await http.patch(
        `/user/${authContext.user.uuid}`,
        formData,
        true,
        { 'Content-Type': 'multipart/form-data' },
      );

      if (responseData && !error) {
        const updatedFields: ApiRecord = { ...responseData };
        updatedFields.phoneNumber =
          asString(responseData.phoneNumber) || asString(responseData.phone_number);
        updatedFields.phoneCountryCode =
          asString(responseData.phoneCountryCode) || asString(responseData.phone_country_code);
        updatedFields.phoneCountryExtension =
          asString(responseData.phoneCountryExtension) ||
          asString(responseData.phone_country_extension);
        updatedFields.phoneCountryName =
          asString(responseData.phoneCountryName) || asString(responseData.phone_country_name);
        updatedFields.profilePicture =
          asString(responseData.profilePicture) || asString(responseData.profile_picture);
        if (updatedFields.profilePicture) {
          updatedFields.profilePicture = `${asString(updatedFields.profilePicture)}?t=${Date.now()}`;
        }

        authContext.updateUser?.(updatedFields);

        setProfilePhoto(asString(updatedFields.profilePicture));
        setProfileName(asString(updatedFields.name));
        setProfilePhone(asString(updatedFields.phoneNumber));
        setProfilePhotoFile(null);
        setProfilePhotoPreview('');
        setProfileEditMode(false);
        refetchBusinessUsers();
        toast.success('Profile updated successfully');
      } else if (error) {
        const messages: string[] = [];
        Object.keys(error).forEach((key) => {
          if (key === 'status' || key === 'url') return;
          const val = error[key];
          if (Array.isArray(val)) {
            messages.push(...val.map(String));
          } else if (typeof val === 'string') {
            messages.push(val);
          }
        });
        toast.error(messages.length > 0 ? messages.join(', ') : 'Failed to update profile');
      }
    } catch {
      toast.error('An error occurred while updating profile');
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const renderTabContent = () => {
    if (!activeTab) return null;

    switch (activeTab) {
      case 'profile-info':
        return (
          <div className="p-5 md:border-b md:border-l md:border-r md:border-paleGrey md:rounded-b-[4px] bg-white">
            <div className="space-y-5">
              <input
                ref={profileFileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/jpg"
                onChange={handleProfilePhotoSelect}
                className="hidden"
              />

              {/* Mobile layout - always editable */}
              <div className="flex flex-col items-center md:hidden">
                <div
                  className="relative w-[120px] h-[120px] cursor-pointer"
                  onClick={() => profileFileInputRef.current?.click()}
                >
                  <div className="w-[120px] h-[120px] rounded-full overflow-hidden bg-gray-100">
                    {profilePhotoPreview || profilePhoto ? (
                      <img
                        key={profilePhotoPreview || profilePhoto}
                        src={profilePhotoPreview || profilePhoto}
                        alt="Profile"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-indigo text-white font-poppins font-semibold text-3xl">
                        {profileName?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                    )}
                  </div>
                  <div className="absolute bottom-0 right-0 w-[32px] h-[32px] rounded-full bg-white flex items-center justify-center shadow-md border border-gray-200">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M13 3h-2.18l-1.64-2H6.82L5.18 3H3C2.45 3 2 3.45 2 4v8c0 .55.45 1 1 1h10c.55 0 1-.45 1-1V4c0-.55-.45-1-1-1zM8 11c-1.93 0-3.5-1.57-3.5-3.5S6.07 4 8 4s3.5 1.57 3.5 3.5S9.93 11 8 11zM8 5.5C6.9 5.5 6 6.4 6 7.5S6.9 9.5 8 9.5s2-.9 2-2S9.1 5.5 8 5.5z"
                        fill="#0102CE"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Desktop layout - view/edit mode */}
              {profileEditMode ? (
                <div className="hidden md:flex items-center gap-4">
                  <div className="w-[110px] h-[110px] rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
                    {profilePhotoPreview || profilePhoto ? (
                      <img
                        key={profilePhotoPreview || profilePhoto}
                        src={profilePhotoPreview || profilePhoto}
                        alt="Profile"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-indigo text-white font-poppins font-semibold text-2xl">
                        {profileName?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="font-poppins font-medium text-[14px] leading-[100%] text-darkBlack mb-3">
                      Profile Photo
                    </p>
                    <p className="font-poppins font-normal text-[14px] leading-[100%] text-grayMedium mb-5">
                      Upload a new profile picture. JPG or PNG, max 5MB.
                    </p>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => profileFileInputRef.current?.click()}
                        className="h-[33px] px-5 py-[6px] rounded-[4px] bg-royalBlue text-white font-poppins font-medium text-[14px] leading-[100%] hover:opacity-90 transition-opacity"
                      >
                        Upload Photo
                      </button>
                      <Render when={!!(profilePhoto || profilePhotoPreview)}>
                        <button
                          type="button"
                          onClick={handleRemoveProfilePhoto}
                          className="h-[33px] px-5 py-[6px] rounded-[40px] font-poppins font-medium text-[14px] leading-[100%] text-center text-redWarning hover:opacity-80 transition-opacity"
                        >
                          Remove
                        </button>
                      </Render>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="hidden md:flex items-center gap-4">
                  <div className="w-[110px] h-[110px] rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
                    {profilePhoto ? (
                      <img
                        key={profilePhoto}
                        src={profilePhoto}
                        alt="Profile"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-indigo text-white font-poppins font-semibold text-2xl">
                        {profileName?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Name - mobile always editable, desktop respects profileEditMode */}
              <div>
                <label className="block mb-2 font-poppins font-normal text-[12px] leading-[100%] text-darkBlack">
                  Name
                </label>
                <input
                  type="text"
                  value={profileName}
                  onChange={(e) => setProfileName(e.target.value)}
                  disabled={false}
                  className={`w-full h-[44px] md:h-[40px] py-[10px] px-[16px] border border-gray200 rounded-[4px] font-poppins font-normal text-[14px] text-darkBlack bg-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 ${
                    !profileEditMode ? 'md:bg-offWhiteBg md:pointer-events-none' : ''
                  }`}
                  placeholder="Enter name"
                />
              </div>

              {/* Phone Number - mobile always editable, desktop view/edit */}
              <div className="md:hidden">
                <PhoneInputWithFlag
                  defaultCountryCode={profilePhoneCountryCode}
                  initialPhoneCountryExtension={profilePhoneCountryExtension}
                  phoneNumber={profilePhone}
                  onPhoneChange={(value) => setProfilePhone(value)}
                  onCountryChange={(option: CountryOption) => {
                    setProfilePhoneCountryCode(option.code);
                    setProfilePhoneCountryExtension(option.callingCode);
                    setProfilePhoneCountryName(option.name);
                  }}
                />
              </div>
              <div className="hidden md:block">
                <PhoneInputWithFlag
                  defaultCountryCode={profilePhoneCountryCode}
                  initialPhoneCountryExtension={profilePhoneCountryExtension}
                  phoneNumber={profilePhone}
                  onPhoneChange={(value) => setProfilePhone(value)}
                  onCountryChange={(option: CountryOption) => {
                    setProfilePhoneCountryCode(option.code);
                    setProfilePhoneCountryExtension(option.callingCode);
                    setProfilePhoneCountryName(option.name);
                  }}
                  disabled={!profileEditMode}
                />
              </div>

              {/* Email (non-editable) */}
              <div>
                <label className="block mb-2 font-poppins font-normal text-[12px] leading-[100%] text-darkBlack">
                  <span className="md:hidden">Email Address</span>
                  <span className="hidden md:inline">Email</span>
                </label>
                <input
                  type="email"
                  value={authContext?.user?.email || ''}
                  disabled
                  className="w-full h-[44px] md:h-[40px] py-[10px] px-[16px] border border-gray200 rounded-[4px] bg-offWhiteBg font-poppins font-normal text-[14px] text-grayMedium cursor-default"
                />
              </div>
            </div>
          </div>
        );
      case 'basic-info':
        return (
          <div className="flex-1 p-4 md:p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div>
                <label className="block mb-2 font-poppins font-normal text-[12px] leading-[100%] tracking-normal text-darkBlack">
                  Business Name
                  <span className="font-poppins font-normal text-[12px] leading-[100%] tracking-normal text-red-500">
                    *
                  </span>
                </label>
                <input
                  type="text"
                  value={businessName}
                  disabled
                  className="w-full h-[44px] md:h-[40px] py-[10px] px-[16px] border border-gray200 rounded-[4px] bg-gray-100 font-poppins font-normal text-[14px] leading-[100%] tracking-normal text-grayMedium cursor-not-allowed"
                  placeholder="Enter business name"
                />
              </div>
              <div>
                <label className="block mb-2 font-poppins font-normal text-[12px] leading-[100%] tracking-normal text-darkBlack">
                  Street Address
                </label>
                <input
                  type="text"
                  value={streetAddress}
                  disabled
                  className="w-full h-[44px] md:h-[40px] py-[10px] px-[16px] border border-gray200 rounded-[4px] bg-gray-100 font-poppins font-normal text-[14px] leading-[100%] tracking-normal text-grayMedium cursor-not-allowed"
                  placeholder="Enter street address"
                />
              </div>
              <div>
                <label className="block mb-2 font-poppins font-normal text-[12px] leading-[100%] tracking-normal text-darkBlack">
                  Country
                  <span className="font-poppins font-normal text-[12px] leading-[100%] tracking-normal text-red-500">
                    *
                  </span>
                </label>
                <SelectField<{ name: string; value: string }>
                  getOptionLabel={(option) => option?.name || ''}
                  getOptionValue={(option) => option?.value || ''}
                  value={selectedCountry}
                  isSearchable={false}
                  placeholder="Select Country"
                  closeMenuOnSelect
                  options={countryOptions}
                  isLoading={isLoadingCountries}
                  isDisabled={true}
                  onChange={(selectedOption) => {
                    setSelectedCountry(selectedOption);
                    setSelectedState(null);
                    setSelectedCity(null);
                  }}
                  styles={{
                    control: (base) => ({
                      ...base,
                      backgroundColor: '#f3f4f6',
                      cursor: 'not-allowed',
                    }),
                    singleValue: (base) => ({
                      ...base,
                      fontFamily: 'Poppins',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '100%',
                      letterSpacing: '0%',
                      color: '#757575',
                    }),
                    placeholder: (base) => ({
                      ...base,
                      fontFamily: 'Poppins',
                      fontWeight: 400,
                      fontSize: '14px',
                      color: '#757575',
                    }),
                    indicatorSeparator: () => ({ display: 'none' }),
                  }}
                />
              </div>
              <div>
                <label className="block mb-2 font-poppins font-normal text-[12px] leading-[100%] tracking-normal text-darkBlack">
                  State
                  <span className="font-poppins font-normal text-[12px] leading-[100%] tracking-normal text-red-500">
                    *
                  </span>
                </label>
                <SelectField<{ name: string; value: string }>
                  getOptionLabel={(option) => option?.name || ''}
                  getOptionValue={(option) => option?.value || ''}
                  value={selectedState}
                  isSearchable={false}
                  placeholder="Select State"
                  closeMenuOnSelect
                  options={stateOptions}
                  isLoading={isLoadingStates}
                  isDisabled={true}
                  onChange={(selectedOption) => {
                    setSelectedState(selectedOption);
                    setSelectedCity(null);
                  }}
                  styles={{
                    control: (base) => ({
                      ...base,
                      backgroundColor: '#f3f4f6',
                      cursor: 'not-allowed',
                    }),
                    singleValue: (base) => ({
                      ...base,
                      fontFamily: 'Poppins',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '100%',
                      letterSpacing: '0%',
                      color: '#757575',
                    }),
                    placeholder: (base) => ({
                      ...base,
                      fontFamily: 'Poppins',
                      fontWeight: 400,
                      fontSize: '14px',
                      color: '#757575',
                    }),
                    indicatorSeparator: () => ({ display: 'none' }),
                  }}
                />
              </div>
              <div>
                <label className="block mb-2 font-poppins font-normal text-[12px] leading-[100%] tracking-normal text-darkBlack">
                  City
                  <span className="font-poppins font-normal text-[12px] leading-[100%] tracking-normal text-red-500">
                    *
                  </span>
                </label>
                <SelectField<{ name: string; value: string }>
                  getOptionLabel={(option) => option?.name || ''}
                  getOptionValue={(option) => option?.value || ''}
                  value={selectedCity}
                  isSearchable={false}
                  placeholder="Select City"
                  closeMenuOnSelect
                  options={cityOptions}
                  isLoading={isLoadingCities}
                  isDisabled={true}
                  onChange={(selectedOption) => setSelectedCity(selectedOption)}
                  styles={{
                    control: (base) => ({
                      ...base,
                      backgroundColor: '#f3f4f6',
                      cursor: 'not-allowed',
                    }),
                    singleValue: (base) => ({
                      ...base,
                      fontFamily: 'Poppins',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '100%',
                      letterSpacing: '0%',
                      color: '#757575',
                    }),
                    placeholder: (base) => ({
                      ...base,
                      fontFamily: 'Poppins',
                      fontWeight: 400,
                      fontSize: '14px',
                      color: '#757575',
                    }),
                    indicatorSeparator: () => ({ display: 'none' }),
                  }}
                />
              </div>
              <div>
                <label className="block mb-2 font-poppins font-normal text-[12px] leading-[100%] tracking-normal text-darkBlack">
                  Zipcode
                  <span className="font-poppins font-normal text-[12px] leading-[100%] tracking-normal text-red-500">
                    *
                  </span>
                </label>
                <input
                  type="text"
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value)}
                  disabled
                  className="w-full h-[44px] md:h-[40px] py-[10px] px-[16px] border border-gray200 rounded-[4px] bg-gray-100 font-poppins font-normal text-[14px] leading-[100%] tracking-normal text-grayMedium cursor-not-allowed"
                  placeholder="Enter zipcode"
                />
              </div>
            </div>
          </div>
        );
      case 'additional-info':
        return (
          <div className="p-4 md:p-6">
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Category */}
                <div>
                  <label className="block mb-2 font-poppins text-xs font-semibold md:font-normal leading-[12px] text-darkBlack">
                    Category
                  </label>
                  <SelectField<{ name: string; value: string }>
                    getOptionLabel={(option) => option?.name || ''}
                    getOptionValue={(option) => option?.value || ''}
                    value={category}
                    isSearchable={false}
                    placeholder="Select Category"
                    closeMenuOnSelect
                    options={categoryOptions}
                    isDisabled={true}
                    onChange={(selectedOption) => setCategory(selectedOption)}
                    styles={{
                      control: (base) => ({
                        ...base,
                        backgroundColor: '#f3f4f6',
                        cursor: 'not-allowed',
                      }),
                      singleValue: (base) => ({
                        ...base,
                        fontFamily: 'Poppins',
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '100%',
                        letterSpacing: '0%',
                        color: '#757575',
                      }),
                      placeholder: (base) => ({
                        ...base,
                        fontFamily: 'Poppins',
                        fontWeight: 400,
                        fontSize: '14px',
                        color: '#757575',
                      }),
                      indicatorSeparator: () => ({ display: 'none' }),
                    }}
                  />
                </div>

                {/* Website */}
                <div>
                  <label className="block mb-2 font-poppins text-xs font-semibold md:font-normal leading-[12px] text-darkBlack">
                    Website
                  </label>
                  <input
                    type="text"
                    placeholder="Enter Website (e.g., https://example.com)"
                    value={website}
                    onChange={(e) => handleWebsiteChange(e.target.value)}
                    disabled={!isEditing}
                    className={`w-full h-[44px] md:h-[40px] py-[10px] px-[16px] border rounded-[4px] bg-[offWhiteBg] font-poppins font-normal text-[14px] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${isEditing ? 'text-darkBlack' : 'text-grayMedium'} ${!isEditing ? 'cursor-not-allowed' : ''} ${validationErrors.website ? 'border-red-500' : 'border-gray200'}`}
                  />
                  {validationErrors.website && (
                    <span className="text-red-500 text-xs mt-1 block">
                      {validationErrors.website}
                    </span>
                  )}
                </div>
              </div>

              {/* Business Phone - Full Width */}
              <div>
                <label className="block mb-2 font-poppins text-xs font-semibold md:font-normal leading-[12px] text-darkBlack">
                  Business Phone
                </label>
                <div className="space-y-3">
                  {businessPhones.map((phone, index) => (
                    <div key={index}>
                      <div className="flex gap-2 items-center">
                        <div className="flex-1">
                          <input
                            type="text"
                            inputMode="numeric"
                            placeholder="Enter phone number (numbers only)"
                            value={phone}
                            onChange={(e) => handleBusinessPhoneChange(index, e.target.value)}
                            onKeyDown={(e) => {
                              // Allow: backspace, delete, tab, escape, enter, arrows
                              if ([8, 9, 27, 13, 46, 37, 38, 39, 40].includes(e.keyCode)) return;
                              // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
                              if ((e.ctrlKey || e.metaKey) && [65, 67, 86, 88].includes(e.keyCode))
                                return;
                              // Allow: numbers, +, -, (, ), space
                              if (/^[0-9+\-() ]$/.test(e.key)) return;
                              // Block everything else
                              e.preventDefault();
                            }}
                            className={`w-full h-[44px] px-3 py-2 border rounded-md focus:outline-none focus:ring-2 placeholder:text-silverGray placeholder:font-poppins placeholder:font-normal placeholder:text-[14px] font-poppins font-normal text-[14px] focus:ring-blue-500 focus:border-blue-500 bg-[offWhiteBg] ${isEditing ? 'text-darkBlack' : 'text-grayMedium'} ${!isEditing ? 'cursor-not-allowed' : ''} ${validationErrors.phones?.[index] ? 'border-red-500' : 'border-gray-300'}`}
                            disabled={!isEditing}
                          />
                          {validationErrors.phones?.[index] && (
                            <span className="text-red-500 text-xs mt-1 block">
                              {validationErrors.phones[index]}
                            </span>
                          )}
                        </div>
                        <Render when={index > 0}>
                          <button
                            type="button"
                            onClick={() => removeBusinessPhone(index)}
                            className="p-2 text-red-500 hover:text-red-700 flex-shrink-0"
                          >
                            <TrashIcon />
                          </button>
                        </Render>
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={addBusinessPhone}
                  disabled={!isEditing}
                  className={`text-sm mt-2 flex items-center ${isEditing ? 'text-blue-600 hover:text-blue-800' : 'text-gray-400 cursor-not-allowed'}`}
                >
                  <div
                    className={`w-[14px] h-[14px] rounded-full flex items-center justify-center mr-1 ${isEditing ? 'bg-blue-600' : 'bg-gray-400'}`}
                  >
                    <svg
                      className="w-2 h-2 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                  </div>
                  Add business phone
                </button>
              </div>

              {/* External Links - Full Width - Desktop: Inline, Mobile: Modal */}
              <div>
                <label className="block mb-2 font-poppins text-xs font-semibold md:font-normal leading-[12px] text-darkBlack">
                  Find Us At
                </label>

                {/* Desktop View - Inline Form Fields */}
                <div className="hidden md:block">
                  <div className="space-y-3">
                    {socialLinks.map((link, index) => (
                      <div key={index} className="flex gap-3 items-start">
                        {/* Platform Dropdown */}
                        <div className="w-48">
                          <SelectField<{ value: string; label: string }>
                            getOptionLabel={(option) => option?.label || ''}
                            getOptionValue={(option) => option?.value || ''}
                            value={
                              link.platform
                                ? platformOptions.find((opt) => opt.value === link.platform)
                                : null
                            }
                            options={getAvailablePlatformOptions(index)}
                            isDisabled={!isEditing}
                            onChange={(selectedOption) => {
                              if (selectedOption) {
                                handleSocialLinkChange(index, 'platform', selectedOption.value);
                              }
                            }}
                            placeholder="Select Platform"
                            closeMenuOnSelect
                            styles={{
                              control: (base) => ({
                                ...base,
                                backgroundColor: 'offWhiteBg',
                                cursor: !isEditing ? 'not-allowed' : 'pointer',
                              }),
                              singleValue: (base) => ({
                                ...base,
                                fontFamily: 'Poppins',
                                fontWeight: 400,
                                fontSize: '14px',
                                color: isEditing ? '#0B1215' : '#757575',
                              }),
                              placeholder: (base) => ({
                                ...base,
                                fontFamily: 'Poppins',
                                fontWeight: 400,
                                fontSize: '14px',
                                color: '#BCBCBF',
                              }),
                            }}
                          />
                        </div>

                        {/* URL Input with Icon */}
                        <div className="flex-1">
                          <div className="relative">
                            {link.platform && (
                              <div
                                className={`absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none ${validationErrors.socialLinks?.[index] ? '-top-[10px]' : 'top-0'}`}
                              >
                                {getPlatformIcon(link.platform)}
                              </div>
                            )}
                            <input
                              type="text"
                              placeholder="Enter the URL"
                              value={link.url}
                              onChange={(e) => handleSocialLinkChange(index, 'url', e.target.value)}
                              className={`w-full h-[42px] ${link.platform ? 'pl-10' : 'pl-3'} pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 placeholder:text-silverGray placeholder:font-poppins placeholder:font-normal placeholder:text-[14px] font-poppins font-normal text-[14px] focus:ring-blue-500 bg-[offWhiteBg] ${isEditing ? 'text-darkBlack' : 'text-grayMedium'} ${!isEditing ? 'cursor-not-allowed' : ''} ${validationErrors.socialLinks?.[index] ? 'border-red-500' : 'border-gray-300'}`}
                              disabled={!isEditing}
                            />
                          </div>
                          {validationErrors.socialLinks?.[index] && (
                            <span className="text-red-500 text-xs mt-1 block">
                              {validationErrors.socialLinks[index]}
                            </span>
                          )}
                        </div>

                        {/* Remove Button */}
                        <Render when={index > 0}>
                          <button
                            type="button"
                            onClick={() => removeSocialLink(index)}
                            className="p-2 text-red-500 hover:text-red-700"
                          >
                            <TrashIcon />
                          </button>
                        </Render>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={addSocialLinkInline}
                    disabled={!isEditing}
                    className={`text-sm mt-2 flex items-center ${isEditing ? 'text-blue-600 hover:text-blue-800' : 'text-gray-400 cursor-not-allowed'}`}
                  >
                    <div
                      className={`w-[14px] h-[14px] rounded-full flex items-center justify-center mr-1 ${isEditing ? 'bg-blue-600' : 'bg-gray-400'}`}
                    >
                      <svg
                        className="w-2 h-2 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                    </div>
                    Add link
                  </button>
                </div>

                {/* Mobile View - Simple input fields with icons */}
                <div className="md:hidden">
                  <Render
                    when={
                      socialLinks.length === 0 || (socialLinks.length === 1 && !socialLinks[0].url)
                    }
                    elseRender={
                      <div className="space-y-3">
                        {socialLinks
                          .filter((link) => link.url)
                          .map((link, index) => (
                            <div key={index} className="relative">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                {getPlatformIcon(link.platform)}
                              </div>
                              <input
                                type="text"
                                value={link.url}
                                readOnly
                                className="w-full h-[44px] pl-10 pr-3 py-2 border border-gray-200 rounded-md bg-[offWhiteBg] font-poppins text-sm text-darkBlack truncate"
                              />
                            </div>
                          ))}
                        <button
                          type="button"
                          onClick={openAddLinkModal}
                          disabled={!isEditing}
                          className={`text-sm mt-2 flex items-center ${isEditing ? 'text-blue-600 hover:text-blue-800' : 'text-gray-400 cursor-not-allowed'}`}
                        >
                          <div
                            className={`w-[14px] h-[14px] rounded-full flex items-center justify-center mr-1 ${isEditing ? 'bg-blue-600' : 'bg-gray-400'}`}
                          >
                            <svg
                              className="w-2 h-2 text-white"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={3}
                                d="M12 4v16m8-8H4"
                              />
                            </svg>
                          </div>
                          Add link
                        </button>
                      </div>
                    }
                  >
                    <div className="border border-gray-200 rounded-lg p-8 flex flex-col items-center justify-center bg-gray-50">
                      <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mb-4">
                        <svg
                          className="w-8 h-8 text-blue-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                          />
                        </svg>
                      </div>
                      <p className="text-gray-500 text-sm mb-4">No links added yet</p>
                      <button
                        type="button"
                        onClick={openAddLinkModal}
                        disabled={!isEditing}
                        className={`text-sm flex items-center ${isEditing ? 'text-blue-600 hover:text-blue-800' : 'text-gray-400 cursor-not-allowed'}`}
                      >
                        <div
                          className={`w-[14px] h-[14px] rounded-full flex items-center justify-center mr-1 ${isEditing ? 'bg-blue-600' : 'bg-gray-400'}`}
                        >
                          <svg
                            className="w-2 h-2 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={3}
                              d="M12 4v16m8-8H4"
                            />
                          </svg>
                        </div>
                        Add link
                      </button>
                    </div>
                  </Render>
                </div>
              </div>

              {/* Add Link Modal - Bottom Sheet Style (Mobile Only) */}
              <Render when={isModalOpen && isMobile}>
                <div className="fixed inset-0 z-50 flex items-end justify-center">
                  {/* Backdrop */}
                  <div
                    className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
                    onClick={() => setIsModalOpen(false)}
                  ></div>

                  {/* Bottom Sheet */}
                  <div className="relative w-full bg-white rounded-t-2xl shadow-xl animate-slide-up max-h-[85vh] overflow-y-auto">
                    <div className="p-6 space-y-4">
                      {/* Modal Header */}
                      <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                        <h3 className="text-xl font-semibold text-gray-900">Add Social Profile</h3>
                        <button
                          onClick={() => setIsModalOpen(false)}
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <XIcon className="w-6 h-6" />
                        </button>
                      </div>

                      {/* Platform Selection */}
                      <div>
                        <label className="block mb-2 font-poppins text-xs font-semibold md:font-normal leading-[12px] text-darkBlack">
                          Choose Type
                        </label>
                        <SelectField<{ value: string; label: string }>
                          getOptionLabel={(option) => option?.label || ''}
                          getOptionValue={(option) => option?.value || ''}
                          value={
                            platformOptions.find((opt) => opt.value === newLink.platform) || null
                          }
                          options={platformOptions.filter(
                            (opt) => !isPlatformAlreadyUsed(opt.value),
                          )}
                          onChange={(selectedOption) => {
                            if (selectedOption) {
                              setNewLink({ ...newLink, platform: selectedOption.value });
                            }
                          }}
                          placeholder="Select platform"
                          closeMenuOnSelect
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          }}
                        />
                      </div>

                      {/* URL or Username Input - only show when platform is selected */}
                      {newLink.platform && (
                        <div>
                          <label className="block mb-2 font-poppins text-xs font-semibold md:font-normal leading-[12px] text-darkBlack">
                            {newLink.platform === 'instagram' || newLink.platform === 'twitter'
                              ? 'Username'
                              : 'URL'}
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              {getPlatformIcon(newLink.platform)}
                            </div>
                            <input
                              type="text"
                              placeholder={
                                newLink.platform === 'instagram' || newLink.platform === 'twitter'
                                  ? 'Enter username'
                                  : newLink.platform === 'facebook'
                                    ? 'Enter Facebook URL'
                                    : newLink.platform === 'linkedin'
                                      ? 'Enter LinkedIn URL'
                                      : 'Enter URL'
                              }
                              value={newLink.url}
                              onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                              className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-silverGray placeholder:font-poppins placeholder:font-normal placeholder:text-[14px] font-poppins font-normal text-[14px] text-grayMedium bg-[offWhiteBg]"
                            />
                          </div>
                        </div>
                      )}

                      {/* Add Button - only show when platform is selected */}
                      {newLink.platform && (
                        <Button
                          type="button"
                          label="Add"
                          onClick={handleAddLink}
                          disabled={!newLink.url.trim()}
                          variant="primary"
                          customClass="w-full !mb-0 !me-0"
                        />
                      )}
                    </div>
                  </div>
                </div>
              </Render>

              {/* Business Hours - Full Width */}
              <div>
                <label className="block mb-2 font-poppins text-xs font-semibold md:font-normal leading-[12px] text-darkBlack">
                  Business Hours
                </label>

                {/* Mobile Layout - Inline like desktop */}
                <div className="md:hidden border border-gray-300 rounded-lg py-4 px-4 w-full bg-white">
                  <div className="space-y-5">
                    {daysOfWeek.map((day) => {
                      const dayKey = day.toLowerCase();
                      const isEnabled = businessHours[dayKey];
                      const dayTimes = businessHoursTimes[dayKey] || [];
                      // Abbreviated day names for mobile
                      const dayAbbrev = day.slice(0, 3);

                      return (
                        <div key={day} className="space-y-2">
                          {/* When disabled - show Closed */}
                          <Render when={!isEnabled}>
                            <div className="flex items-center gap-3">
                              <ToggleButton
                                value={isEnabled}
                                onToggle={() => toggleBusinessHour(dayKey)}
                                label=""
                                disabled={!isEditing}
                              />
                              <span className="w-[50px] font-poppins font-normal text-[14px] text-darkBlack">
                                {dayAbbrev}
                              </span>
                              <span className="font-poppins font-normal text-[14px] text-darkBlue">
                                Closed
                              </span>
                            </div>
                          </Render>

                          {/* When enabled - show time inputs */}
                          <Render when={isEnabled}>
                            {dayTimes.map((slot, index) => (
                              <div key={index} className="flex items-center gap-2">
                                <Render when={index === 0}>
                                  <ToggleButton
                                    value={isEnabled}
                                    onToggle={() => toggleBusinessHour(dayKey)}
                                    label=""
                                    disabled={!isEditing}
                                  />
                                  <span className="w-[50px] font-poppins font-normal text-[14px] text-darkBlack">
                                    {dayAbbrev}
                                  </span>
                                </Render>
                                <Render when={index > 0}>
                                  {/* Spacer for alignment on additional time slots */}
                                  <div className="w-[50px] ml-[52px]"></div>
                                </Render>
                                <div
                                  className={`relative w-[100px] ${isEditing ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                                  onClick={(e) => {
                                    if (!isEditing) return;
                                    const input = e.currentTarget.querySelector('input');
                                    if (input) {
                                      input.showPicker();
                                    }
                                  }}
                                >
                                  <input
                                    type="time"
                                    disabled={!isEditing}
                                    className={`w-full h-[40px] px-2 py-2 border border-gray-200 rounded bg-white font-poppins font-normal text-[14px] text-center focus:outline-none focus:ring-2 focus:ring-blue-500 ${slot.startTime ? (isEditing ? 'text-darkBlack' : 'text-grayMedium') : 'text-transparent'} ${isEditing ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                                    value={slot.startTime || ''}
                                    onChange={(e) =>
                                      handleTimeChange(dayKey, index, 'startTime', e.target.value)
                                    }
                                  />
                                  {!slot.startTime && (
                                    <span className="absolute inset-0 flex items-center justify-center text-silverGray pointer-events-none font-poppins text-sm">
                                      HH:MM
                                    </span>
                                  )}
                                </div>
                                <span className="text-darkBlack text-sm">-</span>
                                <div
                                  className={`relative w-[100px] ${isEditing ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                                  onClick={(e) => {
                                    if (!isEditing) return;
                                    const input = e.currentTarget.querySelector('input');
                                    if (input) {
                                      input.showPicker();
                                    }
                                  }}
                                >
                                  <input
                                    type="time"
                                    disabled={!isEditing}
                                    className={`w-full h-[40px] px-2 py-2 border border-gray-200 rounded bg-white font-poppins font-normal text-[14px] text-center focus:outline-none focus:ring-2 focus:ring-blue-500 ${slot.endTime ? (isEditing ? 'text-darkBlack' : 'text-grayMedium') : 'text-transparent'} ${isEditing ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                                    value={slot.endTime || ''}
                                    onChange={(e) =>
                                      handleTimeChange(dayKey, index, 'endTime', e.target.value)
                                    }
                                  />
                                  {!slot.endTime && (
                                    <span className="absolute inset-0 flex items-center justify-center text-silverGray pointer-events-none font-poppins text-sm">
                                      HH:MM
                                    </span>
                                  )}
                                </div>
                                {/* Plus/Remove button - always show placeholder for alignment */}
                                <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                                  <Render when={dayTimes.length < 2 && index === 0 && isEditing}>
                                    <button
                                      type="button"
                                      onClick={() => addTimeSlot(dayKey)}
                                      className="text-primary"
                                    >
                                      <svg
                                        className="w-7 h-7"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <circle
                                          cx="12"
                                          cy="12"
                                          r="10"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                        />
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M12 8v8M8 12h8"
                                        />
                                      </svg>
                                    </button>
                                  </Render>
                                  <Render when={index > 0 && isEditing}>
                                    <button
                                      type="button"
                                      onClick={() => removeTimeSlot(dayKey, index)}
                                      className="text-coralRed hover:text-red-700"
                                    >
                                      <svg
                                        className="w-5 h-5"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                        />
                                      </svg>
                                    </button>
                                  </Render>
                                  <Render when={!isEditing && dayTimes.length < 2 && index === 0}>
                                    <div className="text-gray-300">
                                      <svg
                                        className="w-7 h-7"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <circle
                                          cx="12"
                                          cy="12"
                                          r="10"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                        />
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M12 8v8M8 12h8"
                                        />
                                      </svg>
                                    </div>
                                  </Render>
                                </div>
                              </div>
                            ))}
                          </Render>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Desktop Layout */}
                <div className="hidden md:block border border-gray-300 rounded-lg py-4 px-5 w-full bg-white">
                  <div className="space-y-4">
                    {daysOfWeek.map((day) => {
                      const dayKey = day.toLowerCase();
                      const isEnabled = businessHours[dayKey];
                      const dayTimes = businessHoursTimes[dayKey] || [];

                      return (
                        <div key={day} className="space-y-2">
                          <Render
                            when={isEnabled && dayTimes.length > 0}
                            elseRender={
                              <div className="flex items-center gap-4 py-1">
                                <div className="w-[100px] font-poppins font-normal text-[15px] leading-[100%] text-darkBlack">
                                  {day}
                                </div>
                                <ToggleButton
                                  value={isEnabled}
                                  onToggle={() => toggleBusinessHour(dayKey)}
                                  label=""
                                  disabled={!isEditing}
                                />
                              </div>
                            }
                          >
                            {dayTimes.map((slot, index) => (
                              <div key={index} className="flex items-center gap-4">
                                <Render when={index === 0}>
                                  <div className="w-[100px] font-poppins font-normal text-[15px] leading-[100%] text-darkBlack">
                                    {day}
                                  </div>
                                  <ToggleButton
                                    value={isEnabled}
                                    onToggle={() => toggleBusinessHour(dayKey)}
                                    label=""
                                    disabled={!isEditing}
                                  />
                                </Render>
                                <Render when={index > 0}>
                                  <div className="w-[100px]"></div>
                                  <div className="w-[52px]"></div>
                                </Render>

                                <div className="flex items-center gap-3">
                                  <div
                                    className={`relative w-[130px] ${isEditing ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                                    onClick={(e) => {
                                      if (!isEditing) return;
                                      const input = e.currentTarget.querySelector('input');
                                      if (input) {
                                        input.showPicker();
                                      }
                                    }}
                                  >
                                    <input
                                      type="time"
                                      disabled={!isEditing}
                                      className={`w-full h-[36px] px-3 py-2 border border-[borderGrey] rounded bg-[offWhiteBg] font-poppins font-normal text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500 ${isEditing ? 'text-darkBlack cursor-pointer' : 'text-grayMedium cursor-not-allowed'}`}
                                      value={slot.startTime || ''}
                                      onChange={(e) =>
                                        handleTimeChange(dayKey, index, 'startTime', e.target.value)
                                      }
                                    />
                                    {!slot.startTime && (
                                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-silverGray pointer-events-none font-poppins text-xs">
                                        HH:MM
                                      </span>
                                    )}
                                  </div>
                                  <span className="text-darkBlack text-sm">-</span>
                                  <div
                                    className={`relative w-[130px] ${isEditing ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                                    onClick={(e) => {
                                      if (!isEditing) return;
                                      const input = e.currentTarget.querySelector('input');
                                      if (input) {
                                        input.showPicker();
                                      }
                                    }}
                                  >
                                    <input
                                      type="time"
                                      disabled={!isEditing}
                                      className={`w-full h-[36px] px-3 py-2 border border-[borderGrey] rounded bg-[offWhiteBg] font-poppins font-normal text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500 ${isEditing ? 'text-darkBlack cursor-pointer' : 'text-grayMedium cursor-not-allowed'}`}
                                      value={slot.endTime || ''}
                                      onChange={(e) =>
                                        handleTimeChange(dayKey, index, 'endTime', e.target.value)
                                      }
                                    />
                                    {!slot.endTime && (
                                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-silverGray pointer-events-none font-poppins text-xs">
                                        HH:MM
                                      </span>
                                    )}
                                  </div>

                                  <Render
                                    when={index === 0 && dayTimes.length < 2}
                                    elseRender={
                                      <Render when={index > 0}>
                                        <button
                                          type="button"
                                          onClick={() => removeTimeSlot(dayKey, index)}
                                          className="text-coralRed hover:text-red-700 p-1 flex-shrink-0"
                                        >
                                          <svg
                                            className="w-5 h-5"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth={2}
                                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                            />
                                          </svg>
                                        </button>
                                      </Render>
                                    }
                                  >
                                    <button
                                      type="button"
                                      onClick={() => addTimeSlot(dayKey)}
                                      disabled={!isEditing}
                                      className={`p-1 flex-shrink-0 ${isEditing ? 'text-googleBlue hover:text-blue-700' : 'text-gray-400 cursor-not-allowed'}`}
                                    >
                                      <svg
                                        className="w-5 h-5"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <circle
                                          cx="12"
                                          cy="12"
                                          r="10"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                        />
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M12 8v8M8 12h8"
                                        />
                                      </svg>
                                    </button>
                                  </Render>
                                </div>
                              </div>
                            ))}
                          </Render>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Business Hours Note */}
              <div>
                <textarea
                  rows={1}
                  placeholder="Enter business hours note"
                  value={businessHoursNote}
                  onChange={(e) => setBusinessHoursNote(e.target.value)}
                  disabled={!isEditing}
                  className={`w-full max-w-[1320px] h-[47px] rounded border border-gray-300 p-3 font-poppins font-normal text-[14px] placeholder:text-silverGray placeholder:font-poppins placeholder:font-normal placeholder:text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none bg-[offWhiteBg] ${isEditing ? 'text-darkBlack' : 'text-grayMedium cursor-not-allowed'}`}
                />
              </div>
            </div>
          </div>
        );
      case 'business-photos':
        // Photos are always editable - no edit mode toggle needed
        const isEditingPhotos = true;
        const totalPhotos = businessPhotos.length + newPhotos.length;
        const showUploadArea = isEditingPhotos && totalPhotos < MAX_BANNER_IMAGE_COUNT;

        return (
          <>
            <div className="p-4 md:p-6">
              {/* Drag & Drop Upload Area - Show when editing and photos < 5 */}
              <Render when={showUploadArea}>
                <div
                  className={`rounded-lg p-6 sm:p-8 text-center mb-6 transition-colors cursor-pointer ${
                    isDragOver ? 'bg-royalBlue/5' : 'bg-white'
                  }`}
                  style={{
                    backgroundImage: isDragOver
                      ? `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='8' ry='8' stroke='%237F00FF' stroke-width='2' stroke-dasharray='12%2c 8' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")`
                      : `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='8' ry='8' stroke='%23EDEDF0' stroke-width='2' stroke-dasharray='12%2c 8' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")`,
                  }}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleFileInputChange}
                    className="hidden"
                  />
                  <div className="flex flex-col items-center space-y-3">
                    {/* Upload Icon */}
                    <div className="flex items-center justify-center">
                      <svg
                        className="w-12 h-12 sm:w-16 sm:h-16"
                        viewBox="0 0 60 60"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M52.425 2.5H45.075C41.9 2.5 40 4.4 40 7.575V14.925C40 18.1 41.9 20 45.075 20H52.425C55.6 20 57.5 18.1 57.5 14.925V7.575C57.5 4.4 55.6 2.5 52.425 2.5ZM52.975 10.775C52.675 11.075 52.275 11.225 51.875 11.225C51.475 11.225 51.075 11.075 50.775 10.775L50.325 10.325V15.925C50.325 16.8 49.625 17.5 48.75 17.5C47.875 17.5 47.175 16.8 47.175 15.925V10.325L46.725 10.775C46.125 11.375 45.125 11.375 44.525 10.775C43.925 10.175 43.925 9.175 44.525 8.575L47.65 5.45C47.775 5.325 47.95 5.225 48.125 5.15C48.175 5.125 48.225 5.125 48.275 5.1C48.4 5.05 48.525 5.025 48.675 5.025H48.825C49 5.025 49.15 5.05 49.325 5.125H49.375C49.55 5.2 49.7 5.3 49.825 5.425C49.85 5.45 49.85 5.45 49.875 5.45L53 8.575C53.6 9.175 53.6 10.175 52.975 10.775Z"
                          fill="#0102CE"
                        />
                        <path
                          d="M22.5003 25.9501C25.7865 25.9501 28.4502 23.2861 28.4502 20C28.4502 16.7139 25.7865 14.05 22.5003 14.05C19.2142 14.05 16.5503 16.7139 16.5503 20C16.5503 23.2861 19.2142 25.9501 22.5003 25.9501Z"
                          fill="#0102CE"
                        />
                        <path
                          d="M52.425 20H51.25V31.525L50.925 31.25C48.975 29.575 45.825 29.575 43.875 31.25L33.475 40.175C31.525 41.85 28.375 41.85 26.425 40.175L25.575 39.475C23.8 37.925 20.975 37.775 18.975 39.125L9.625 45.4C9.075 44 8.75 42.375 8.75 40.475V19.525C8.75 12.475 12.475 8.75 19.525 8.75H40V7.575C40 6.575 40.175 5.725 40.575 5H19.525C10.425 5 5 10.425 5 19.525V40.475C5 43.2 5.475 45.575 6.4 47.575C8.55 52.325 13.15 55 19.525 55H40.475C49.575 55 55 49.575 55 40.475V19.425C54.275 19.825 53.425 20 52.425 20Z"
                          fill="#0102CE"
                        />
                      </svg>
                    </div>
                    <div className="space-y-1">
                      <p className="text-gray-700 text-sm sm:text-base font-poppins">
                        Drag & Drop or{' '}
                        <span className="text-royalBlue font-medium">Choose file</span> to upload
                      </p>
                      <p className="text-gray-500 text-xs sm:text-sm font-poppins">
                        You can upload {remainingPhotoSlots()} more photo
                        {remainingPhotoSlots() > 1 ? 's' : ''} (max {MAX_BANNER_IMAGE_COUNT})
                      </p>
                    </div>
                    <p className="text-gray-400 text-[11px] sm:text-xs italic leading-relaxed px-2 sm:px-0 font-poppins">
                      For the best result upload an image of landscape orientation with a size of
                      350 × 150 px (Aspect ratio 2:1)
                    </p>
                  </div>
                </div>
              </Render>

              {/* Empty State - When no photos and not in edit mode */}
              <Render
                when={businessPhotos.length === 0 && newPhotos.length === 0 && !isEditingPhotos}
              >
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <PiEye className="w-10 h-10 text-gray-400" />
                  </div>
                  <p className="text-gray-500 font-poppins text-[14px] mb-2">No photos available</p>
                  <p className="text-gray-400 font-poppins text-[12px]">
                    Click Update to add business photos
                  </p>
                </div>
              </Render>

              {/* Photos Grid - Show when there are photos */}
              <Render when={businessPhotos.length > 0 || newPhotos.length > 0}>
                {/* Mobile Layout - Single column with Drag & Drop */}
                <div className="md:hidden space-y-4">
                  {getAllPhotos().map((photoItem, index) => {
                    const isExisting = photoItem.type === 'existing';
                    const photoData = isExisting
                      ? businessPhotos.find((p) => p.uuid === photoItem.uuid)
                      : newPhotos.find((p) => p.uuid === photoItem.uuid);
                    const isDragging = draggedPhotoIndex === index;
                    const isDragTarget = dragOverIndex === index;

                    if (isExisting && photoData) {
                      const existingPhoto = photoData as { id: number; url: string; uuid: string };
                      return (
                        <div
                          key={`mobile-existing-${existingPhoto.id}`}
                          ref={(el) => (photoRefs.current[index] = el)}
                          className={`bg-white rounded-lg overflow-hidden border border-[borderGrey] transition-all duration-200 ${
                            isDragging ? 'opacity-50 scale-95' : ''
                          } ${isDragTarget ? 'ring-2 ring-royalBlue ring-offset-2' : ''} ${
                            isEditingPhotos ? 'touch-none' : ''
                          }`}
                          draggable={isEditingPhotos}
                          onDragStart={(e) => isEditingPhotos && handlePhotoDragStart(e, index)}
                          onDragEnd={handlePhotoDragEnd}
                          onDragOver={(e) => isEditingPhotos && handlePhotoDragOver(e, index)}
                          onDragLeave={handlePhotoDragLeave}
                          onDrop={(e) => isEditingPhotos && handlePhotoDrop(e, index)}
                          onTouchStart={(e) => isEditingPhotos && handleTouchStart(e, index)}
                          onTouchMove={(e) => isEditingPhotos && handleTouchMove(e)}
                          onTouchEnd={() => isEditingPhotos && handleTouchEnd()}
                        >
                          <div className="relative w-full aspect-[16/10]">
                            <img
                              src={existingPhoto.url}
                              alt={`Business Photo ${existingPhoto.id}`}
                              className="w-full h-full object-cover pointer-events-none"
                            />
                            {isEditingPhotos && (
                              <div className="absolute top-2 left-2 bg-black/50 text-white px-2 py-1 rounded text-xs font-poppins flex items-center gap-1.5">
                                <svg
                                  width="12"
                                  height="12"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <path d="M8 6h8M8 12h8M8 18h8" strokeLinecap="round" />
                                </svg>
                                <span className={PHOTO_NUMBER_BADGE_CLASS}>{index + 1}</span>
                                Hold & Drag
                              </div>
                            )}
                          </div>
                          <div className="flex items-center justify-between p-3 border-t border-[borderGrey] bg-[offWhiteBg]">
                            <button
                              onClick={() => handlePreviewPhoto(existingPhoto)}
                              className="text-previewBlue hover:opacity-80 text-[14px] font-medium font-poppins flex items-center gap-2"
                            >
                              <PiEye className="w-4 h-4" />
                              Preview
                            </button>
                            <Render when={isEditingPhotos}>
                              <button
                                onClick={() => handleDeletePhoto(existingPhoto)}
                                className="text-[orangeRed] hover:text-red-700 text-[14px] font-medium font-poppins flex items-center gap-2"
                              >
                                <PiTrash className="w-4 h-4" />
                                Delete
                              </button>
                            </Render>
                          </div>
                        </div>
                      );
                    } else if (!isExisting && photoData) {
                      const newPhoto = photoData as BusinessEditPhotoProps;
                      return (
                        <div
                          key={`mobile-new-${newPhoto.uuid}`}
                          ref={(el) => (photoRefs.current[index] = el)}
                          className={`bg-white rounded-lg overflow-hidden border border-[borderGrey] relative transition-all duration-200 ${
                            isDragging ? 'opacity-50 scale-95' : ''
                          } ${isDragTarget ? 'ring-2 ring-royalBlue ring-offset-2' : ''} ${
                            isEditingPhotos && newPhoto.status === ImageStatus.UPLOADED
                              ? 'touch-none'
                              : ''
                          }`}
                          draggable={isEditingPhotos && newPhoto.status === ImageStatus.UPLOADED}
                          onDragStart={(e) =>
                            isEditingPhotos &&
                            newPhoto.status === ImageStatus.UPLOADED &&
                            handlePhotoDragStart(e, index)
                          }
                          onDragEnd={handlePhotoDragEnd}
                          onDragOver={(e) => isEditingPhotos && handlePhotoDragOver(e, index)}
                          onDragLeave={handlePhotoDragLeave}
                          onDrop={(e) => isEditingPhotos && handlePhotoDrop(e, index)}
                          onTouchStart={(e) =>
                            isEditingPhotos &&
                            newPhoto.status === ImageStatus.UPLOADED &&
                            handleTouchStart(e, index)
                          }
                          onTouchMove={(e) => isEditingPhotos && handleTouchMove(e)}
                          onTouchEnd={() => isEditingPhotos && handleTouchEnd()}
                        >
                          <div className="relative w-full aspect-[16/10]">
                            <img
                              src={newPhoto.photoUrl}
                              alt="New photo"
                              className={`w-full h-full object-cover pointer-events-none ${newPhoto.status === ImageStatus.UPLOADING ? 'opacity-50' : ''}`}
                            />
                            {newPhoto.status === ImageStatus.UPLOADING && (
                              <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-royalBlue"></div>
                              </div>
                            )}
                            {newPhoto.status === ImageStatus.FAILED && (
                              <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                                <span className="text-white bg-red-500 px-2 py-1 rounded text-xs">
                                  Failed
                                </span>
                              </div>
                            )}
                            {isEditingPhotos && newPhoto.status === ImageStatus.UPLOADED && (
                              <div className="absolute top-2 left-2 bg-black/50 text-white px-2 py-1 rounded text-xs font-poppins flex items-center gap-1.5">
                                <svg
                                  width="12"
                                  height="12"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <path d="M8 6h8M8 12h8M8 18h8" strokeLinecap="round" />
                                </svg>
                                <span className={PHOTO_NUMBER_BADGE_CLASS}>{index + 1}</span>
                                Hold & Drag
                              </div>
                            )}
                          </div>
                          <div className="flex items-center justify-between p-3 border-t border-[borderGrey] bg-[offWhiteBg]">
                            {newPhoto.status === ImageStatus.FAILED ? (
                              <button
                                onClick={() => retryUpload(newPhoto)}
                                className="text-royalBlue hover:opacity-80 text-[14px] font-medium font-poppins flex items-center gap-2"
                              >
                                Retry
                              </button>
                            ) : newPhoto.status === ImageStatus.UPLOADING ? (
                              <span className="text-grayMedium text-[14px] font-poppins">
                                Uploading...
                              </span>
                            ) : (
                              <button
                                onClick={() => {
                                  setPreviewImageUrl(newPhoto.photoUrl);
                                  setShowImagePreview(true);
                                }}
                                className="text-previewBlue hover:opacity-80 text-[14px] font-medium font-poppins flex items-center gap-2"
                              >
                                <PiEye className="w-4 h-4" />
                                Preview
                              </button>
                            )}
                            <button
                              onClick={() => removeNewPhoto(newPhoto.uuid)}
                              className="text-[orangeRed] hover:text-red-700 text-[14px] font-medium font-poppins flex items-center gap-2"
                            >
                              <PiTrash className="w-4 h-4" />
                              Delete
                            </button>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>

                {/* Desktop Layout - 2 Column Grid with Drag & Drop Reordering */}
                <div className="hidden md:grid md:grid-cols-2 gap-6">
                  {getAllPhotos().map((photoItem, index) => {
                    const isExisting = photoItem.type === 'existing';
                    const photoData = isExisting
                      ? businessPhotos.find((p) => p.uuid === photoItem.uuid)
                      : newPhotos.find((p) => p.uuid === photoItem.uuid);
                    const isDragging = draggedPhotoIndex === index;
                    const isDragTarget = dragOverIndex === index;

                    if (isExisting && photoData) {
                      const existingPhoto = photoData as { id: number; url: string; uuid: string };
                      return (
                        <div
                          key={`existing-${existingPhoto.id}`}
                          className={`flex flex-col rounded-lg overflow-hidden border border-[borderGrey] transition-all duration-200 ${
                            isDragging ? 'opacity-50 scale-95' : ''
                          } ${isDragTarget ? 'ring-2 ring-[indigoPurple] ring-offset-2' : ''} ${
                            isEditingPhotos ? 'cursor-grab active:cursor-grabbing' : ''
                          }`}
                          draggable={isEditingPhotos}
                          onDragStart={(e) => isEditingPhotos && handlePhotoDragStart(e, index)}
                          onDragEnd={handlePhotoDragEnd}
                          onDragOver={(e) => isEditingPhotos && handlePhotoDragOver(e, index)}
                          onDragLeave={handlePhotoDragLeave}
                          onDrop={(e) => isEditingPhotos && handlePhotoDrop(e, index)}
                        >
                          <div className="relative w-full overflow-hidden aspect-[16/10]">
                            <img
                              src={existingPhoto.url}
                              alt={`Business Photo ${existingPhoto.id}`}
                              className="w-full h-full object-cover pointer-events-none"
                            />
                            {isEditingPhotos && (
                              <div className="absolute top-2 left-2 bg-black/50 text-white px-2 py-1 rounded text-xs font-poppins flex items-center gap-1.5">
                                <svg
                                  width="12"
                                  height="12"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <path d="M8 6h8M8 12h8M8 18h8" strokeLinecap="round" />
                                </svg>
                                <span className={PHOTO_NUMBER_BADGE_CLASS}>{index + 1}</span>
                                Drag to reorder
                              </div>
                            )}
                          </div>
                          <div className="flex items-center justify-between p-3 bg-[offWhiteBg]">
                            <button
                              onClick={() => handlePreviewPhoto(existingPhoto)}
                              className="text-previewBlue hover:opacity-80 text-[14px] font-medium font-poppins transition-all"
                            >
                              Preview
                            </button>
                            <Render when={isEditingPhotos}>
                              <button
                                onClick={() => handleDeletePhoto(existingPhoto)}
                                className="text-[orangeRed] hover:text-red-700 text-[14px] font-medium font-poppins transition-all"
                              >
                                Delete
                              </button>
                            </Render>
                          </div>
                        </div>
                      );
                    } else if (!isExisting && photoData) {
                      const newPhoto = photoData as BusinessEditPhotoProps;
                      return (
                        <div
                          key={`new-${newPhoto.uuid}`}
                          className={`flex flex-col rounded-lg overflow-hidden border border-[borderGrey] transition-all duration-200 ${
                            isDragging ? 'opacity-50 scale-95' : ''
                          } ${isDragTarget ? 'ring-2 ring-[indigoPurple] ring-offset-2' : ''} ${
                            isEditingPhotos && newPhoto.status === ImageStatus.UPLOADED
                              ? 'cursor-grab active:cursor-grabbing'
                              : ''
                          }`}
                          draggable={isEditingPhotos && newPhoto.status === ImageStatus.UPLOADED}
                          onDragStart={(e) =>
                            isEditingPhotos &&
                            newPhoto.status === ImageStatus.UPLOADED &&
                            handlePhotoDragStart(e, index)
                          }
                          onDragEnd={handlePhotoDragEnd}
                          onDragOver={(e) => isEditingPhotos && handlePhotoDragOver(e, index)}
                          onDragLeave={handlePhotoDragLeave}
                          onDrop={(e) => isEditingPhotos && handlePhotoDrop(e, index)}
                        >
                          <div className="relative w-full overflow-hidden aspect-[16/10]">
                            <img
                              src={newPhoto.photoUrl}
                              alt="New photo"
                              className={`w-full h-full object-cover pointer-events-none ${newPhoto.status === ImageStatus.UPLOADING ? 'opacity-50' : ''}`}
                            />
                            {newPhoto.status === ImageStatus.UPLOADING && (
                              <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-royalBlue"></div>
                              </div>
                            )}
                            {newPhoto.status === ImageStatus.FAILED && (
                              <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                                <span className="text-white bg-red-500 px-3 py-1 rounded text-sm">
                                  Upload Failed
                                </span>
                              </div>
                            )}
                            {isEditingPhotos && newPhoto.status === ImageStatus.UPLOADED && (
                              <div className="absolute top-2 left-2 bg-black/50 text-white px-2 py-1 rounded text-xs font-poppins flex items-center gap-1.5">
                                <svg
                                  width="12"
                                  height="12"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <path d="M8 6h8M8 12h8M8 18h8" strokeLinecap="round" />
                                </svg>
                                <span className={PHOTO_NUMBER_BADGE_CLASS}>{index + 1}</span>
                                Drag to reorder
                              </div>
                            )}
                          </div>
                          <div className="flex items-center justify-between p-3 bg-[offWhiteBg]">
                            {newPhoto.status === ImageStatus.FAILED ? (
                              <button
                                onClick={() => retryUpload(newPhoto)}
                                className="text-[indigoPurple] hover:opacity-80 text-[14px] font-medium font-poppins transition-all"
                              >
                                Retry
                              </button>
                            ) : newPhoto.status === ImageStatus.UPLOADING ? (
                              <span className="text-grayMedium text-[14px] font-poppins flex items-center gap-2">
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-[indigoPurple]"></div>
                                Uploading...
                              </span>
                            ) : (
                              <button
                                onClick={() => {
                                  setPreviewImageUrl(newPhoto.photoUrl);
                                  setShowImagePreview(true);
                                }}
                                className="text-previewBlue hover:opacity-80 text-[14px] font-medium font-poppins transition-all"
                              >
                                Preview
                              </button>
                            )}
                            <button
                              onClick={() => removeNewPhoto(newPhoto.uuid)}
                              className="text-[orangeRed] hover:text-red-700 text-[14px] font-medium font-poppins transition-all"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
              </Render>
            </div>

            {/* Image Preview Modal - rendered via portal to escape stacking context */}
            {showImagePreview &&
              createPortal(
                <div
                  className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-6"
                  onClick={() => setShowImagePreview(false)}
                >
                  <div
                    className="relative max-w-[85vw] max-h-[85vh] flex items-center justify-center"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      onClick={() => setShowImagePreview(false)}
                      className="absolute -top-3 -right-3 z-10 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors"
                    >
                      <IoMdClose size={24} className="text-gray-700" />
                    </button>
                    <img
                      src={previewImageUrl}
                      alt="Preview"
                      className="max-w-full max-h-[85vh] w-auto h-auto object-contain rounded-lg shadow-2xl"
                    />
                  </div>
                </div>,
                document.body,
              )}

            {/* Delete Confirmation Modal */}
            <Render when={showDeleteConfirm}>
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                <div className="w-[416px] max-w-[90vw] bg-white rounded-[12px] pt-8 pb-8 px-5 flex flex-col items-center gap-10">
                  {/* Warning Icon */}
                  <svg
                    width="50"
                    height="50"
                    viewBox="0 0 50 50"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      opacity="0.2"
                      d="M43.75 25C43.75 28.7084 42.6503 32.3335 40.5901 35.4169C38.5298 38.5004 35.6014 40.9036 32.1753 42.3227C28.7492 43.7419 24.9792 44.1132 21.3421 43.3897C17.7049 42.6663 14.364 40.8805 11.7418 38.2583C9.11952 35.636 7.33376 32.2951 6.61028 28.6579C5.88681 25.0208 6.25812 21.2508 7.67727 17.8247C9.09641 14.3986 11.4996 11.4702 14.5831 9.40995C17.6665 7.34967 21.2916 6.25 25 6.25C29.9728 6.25 34.742 8.22544 38.2583 11.7417C41.7746 15.2581 43.75 20.0272 43.75 25Z"
                      fill="#FB4C2F"
                    />
                    <path
                      d="M25 4.6875C20.9826 4.6875 17.0554 5.87881 13.715 8.11077C10.3746 10.3427 7.77111 13.5151 6.23371 17.2267C4.6963 20.9384 4.29405 25.0225 5.07781 28.9628C5.86157 32.903 7.79615 36.5224 10.6369 39.3631C13.4777 42.2039 17.097 44.1384 21.0372 44.9222C24.9775 45.706 29.0616 45.3037 32.7733 43.7663C36.4849 42.2289 39.6573 39.6254 41.8892 36.285C44.1212 32.9446 45.3125 29.0174 45.3125 25C45.3068 19.6145 43.1649 14.4513 39.3568 10.6432C35.5487 6.83507 30.3855 4.69319 25 4.6875ZM25 42.1875C21.6006 42.1875 18.2776 41.1795 15.4511 39.2909C12.6247 37.4023 10.4217 34.718 9.12083 31.5774C7.81995 28.4368 7.47958 24.9809 8.14276 21.6469C8.80595 18.3128 10.4429 15.2503 12.8466 12.8466C15.2503 10.4429 18.3128 8.80594 21.6469 8.14275C24.9809 7.47957 28.4368 7.81994 31.5774 9.12082C34.718 10.4217 37.4023 12.6247 39.2909 15.4511C41.1795 18.2776 42.1875 21.6006 42.1875 25C42.1823 29.5568 40.3699 33.9255 37.1477 37.1477C33.9255 40.3699 29.5568 42.1823 25 42.1875ZM23.4375 26.5625V15.625C23.4375 15.2106 23.6021 14.8132 23.8952 14.5201C24.1882 14.2271 24.5856 14.0625 25 14.0625C25.4144 14.0625 25.8118 14.2271 26.1049 14.5201C26.3979 14.8132 26.5625 15.2106 26.5625 15.625V26.5625C26.5625 26.9769 26.3979 27.3743 26.1049 27.6674C25.8118 27.9604 25.4144 28.125 25 28.125C24.5856 28.125 24.1882 27.9604 23.8952 27.6674C23.6021 27.3743 23.4375 26.9769 23.4375 26.5625ZM27.3438 33.5938C27.3438 34.0573 27.2063 34.5104 26.9488 34.8959C26.6912 35.2813 26.3252 35.5817 25.8969 35.7591C25.4687 35.9365 24.9974 35.9829 24.5428 35.8925C24.0881 35.802 23.6705 35.5788 23.3427 35.251C23.0149 34.9233 22.7917 34.5056 22.7013 34.051C22.6109 33.5963 22.6573 33.1251 22.8347 32.6968C23.0121 32.2686 23.3125 31.9025 23.6979 31.645C24.0833 31.3875 24.5365 31.25 25 31.25C25.6216 31.25 26.2178 31.4969 26.6573 31.9365C27.0968 32.376 27.3438 32.9721 27.3438 33.5938Z"
                      fill="#FB4C2F"
                    />
                  </svg>

                  {/* Content */}
                  <div className="flex flex-col items-center gap-4">
                    <h3 className="font-inter font-medium text-[20px] leading-[100%] text-center text-darkBlack">
                      Are you sure you want to delete?
                    </h3>
                    <p className="font-poppins font-normal text-[14px] leading-[100%] text-center text-grayMedium">
                      Are you sure you want to delete this uploaded image?
                    </p>
                  </div>

                  {/* Buttons */}
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={cancelDeletePhoto}
                      className="h-[38px] px-5 py-[10px] rounded-[40px] border border-[royalBlue] font-poppins font-medium text-[14px] text-[royalBlue] hover:bg-[royalBlue]/5 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={confirmDeletePhoto}
                      className="h-[38px] px-5 py-[10px] rounded-[40px] bg-redWarning font-poppins font-medium text-[14px] text-white hover:bg-redWarning/90 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </Render>
          </>
        );
      case 'pin-management':
        return (
          <div className="flex-1">
            <MerchantPin />
          </div>
        );
      case 'payment-management':
        return (
          <div className="flex-1 p-4 md:p-6 overflow-visible">
            <div className="flex flex-col gap-8">
              {/* Saved Cards Section - only show after data loads and card exists */}
              <Render when={isPaymentDataReady && !!defaultPaymentMethod}>
                <div>
                  <div className="text-xl md:text-xl text-darkBlue mb-4">Saved cards</div>
                  <CustomerCardDetails
                    defaultPaymentMethod={defaultPaymentMethod}
                    onEdit={onEditCard}
                  />
                  <Render when={(!!clientSecret && isEditCard) || isCreatingIntent}>
                    <ModalComponent
                      showClose
                      renderHeader={
                        <>
                          {!isCreatingIntent ? (
                            <div className="text-darkBlue text-xl font-semibold">Card Details</div>
                          ) : null}
                        </>
                      }
                      containerClass="md:w-[688px] md:h-[348px] h-[540px] w-[320px] !md:m-0 !p-0"
                      onClose={onCancelEditCard}
                    >
                      {isCreatingIntent ? (
                        <div className="flex justify-center item-center w-full">
                          <Loader />
                        </div>
                      ) : (
                        <Elements stripe={stripePromise} key={clientSecret}>
                          <EditCardForm
                            clientSecret={clientSecret}
                            onCancel={onCancelEditCard}
                            onSuccess={onCardUpdateSuccess}
                            businessUuid={storedBusiness?.uuid || ''}
                          />
                        </Elements>
                      )}
                    </ModalComponent>
                  </Render>
                </div>
              </Render>

              {/* Payout Methods Section */}
              <PayoutMethods />
            </div>
          </div>
        );
      case 'user-management':
        const handleInviteUser = async () => {
          if (!inviteEmail.trim() || !storedBusiness?.uuid) return;
          try {
            await inviteUser({
              businessUuid: storedBusiness.uuid,
              email: inviteEmail.trim(),
            }).unwrap();
            toast.success(`Your invite is sent to ${inviteEmail.trim()}`);
            setShowInviteModal(false);
            setInviteEmail('');
            refetchInvitations();
          } catch (error) {
            const apiError = error as ApiErrorResponse;
            // Handle error - email field can be an array from API
            const emailError = apiError?.data?.email;
            const errorMessage =
              apiError?.data?.message ||
              (Array.isArray(emailError) ? emailError[0] : emailError) ||
              'Failed to send invitation';
            toast.error(errorMessage);
          }
        };

        const handleResendInvitation = async (email: string) => {
          if (!storedBusiness?.uuid) return;
          try {
            // Use invite API to resend (re-invite with same email)
            await inviteUser({
              businessUuid: storedBusiness.uuid,
              email,
            }).unwrap();
            toast.success(`Your invite is sent to ${email}`);
            setInvitationMenuOpen(null);
            refetchInvitations();
          } catch (error) {
            const apiError = error as ApiErrorResponse;
            const emailError = apiError?.data?.email;
            const errorMessage =
              apiError?.data?.message ||
              (Array.isArray(emailError) ? emailError[0] : emailError) ||
              'Failed to resend invitation';
            toast.error(errorMessage);
          }
        };

        const _handleCancelInvitation = async (invitationUuid: string) => {
          if (!storedBusiness?.uuid) return;
          try {
            await cancelInvitation({
              businessUuid: storedBusiness.uuid,
              invitationUuid,
            }).unwrap();
            toast.success('Invitation cancelled');
            setInvitationMenuOpen(null);
            refetchInvitations();
          } catch (error) {
            const apiError = error as ApiErrorResponse;
            toast.error(apiError?.data?.message || 'Failed to cancel invitation');
          }
        };

        return (
          <div className="p-4 md:p-6">
            {/* Sub-tabs: Users | Invitations */}
            <div className="flex w-full md:w-fit mb-6 rounded-[10px] p-1.5 bg-[#F3F4F6]">
              <button
                onClick={() => setUserManagementSubTab('users')}
                className={`flex-1 md:flex-none md:w-[180px] h-[44px] py-3 px-6 font-poppins text-[14px] leading-[20px] text-center transition-colors rounded-lg ${
                  userManagementSubTab === 'users'
                    ? 'bg-royalBlue text-white font-medium'
                    : 'text-[#9CA3AF] font-normal bg-transparent'
                }`}
              >
                Users
              </button>
              <button
                onClick={() => setUserManagementSubTab('invitations')}
                className={`flex-1 md:flex-none md:w-[180px] h-[44px] py-3 px-6 font-poppins text-[14px] leading-[20px] text-center transition-colors rounded-lg ${
                  userManagementSubTab === 'invitations'
                    ? 'bg-royalBlue text-white font-medium'
                    : 'text-[#9CA3AF] font-normal bg-transparent'
                }`}
              >
                Invitations
              </button>
            </div>

            {/* Users Tab Content */}
            <Render when={userManagementSubTab === 'users'}>
              <Render when={isLoadingUsers}>
                <div className="flex justify-center py-8">
                  <Loader size={6} />
                </div>
              </Render>
              <Render when={!isLoadingUsers}>
                <div className="flex flex-col gap-3">
                  {businessUsers.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="font-poppins text-gray-500">No users found</p>
                    </div>
                  ) : (
                    businessUsers.map((user) => (
                      <div
                        key={user.uuid}
                        className="flex items-center w-full p-4 bg-white border border-borderGrey rounded-[8px] hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          {/* Avatar - Same as Invitations */}
                          <div className="w-10 h-10 rounded-[6px] overflow-hidden bg-paleGreyTwo flex-shrink-0 flex items-center justify-center">
                            {user.avatar ? (
                              <img
                                key={user.avatar}
                                src={user.avatar}
                                alt={user.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="font-poppins font-semibold text-[16px] text-royalBlue">
                                {user.name
                                  ? user.name.charAt(0).toUpperCase()
                                  : user.email.charAt(0).toUpperCase()}
                              </span>
                            )}
                          </div>
                          {/* User Info */}
                          <div className="flex flex-col gap-1">
                            <span className="font-poppins font-semibold text-[14px] text-darkBlue">
                              {user.name || user.email.split('@')[0]}
                            </span>
                            <span className="font-poppins font-normal text-[12px] text-grayMedium">
                              {user.email}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </Render>
            </Render>

            {/* Invitations Tab Content */}
            <Render when={userManagementSubTab === 'invitations'}>
              <Render when={isLoadingInvitations}>
                <div className="flex justify-center py-8">
                  <Loader size={6} />
                </div>
              </Render>
              <Render when={!isLoadingInvitations}>
                <div className="flex flex-col gap-3 pb-16">
                  {invitations.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="font-poppins text-gray-500">No pending invitations</p>
                    </div>
                  ) : (
                    invitations.map((invitation) => (
                      <div
                        key={invitation.uuid}
                        className="flex items-center justify-between w-full p-4 bg-white border border-borderGrey rounded-[8px] hover:bg-gray-50 transition-colors relative"
                      >
                        <div className="flex items-center gap-4">
                          {/* Letter Avatar */}
                          <div className="w-10 h-10 rounded-[6px] flex items-center justify-center flex-shrink-0 bg-paleGreyTwo">
                            <span className="font-poppins font-semibold text-[16px] text-royalBlue">
                              {invitation.email.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          {/* Email */}
                          <span className="font-poppins font-normal text-[14px] text-darkBlue">
                            {invitation.email}
                          </span>
                        </div>
                        {/* Three dots menu */}
                        <div className="relative">
                          <button
                            onClick={() => {
                              // Mobile: Open bottom sheet modal directly
                              if (window.innerWidth < 768) {
                                setResendEmail(invitation.email);
                                setShowResendModal(true);
                              } else {
                                // Desktop: Toggle dropdown
                                setInvitationMenuOpen(
                                  invitationMenuOpen === invitation.uuid ? null : invitation.uuid,
                                );
                              }
                            }}
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                          >
                            <svg
                              width="4"
                              height="16"
                              viewBox="0 0 4 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <circle cx="2" cy="2" r="2" fill="#6B7280" />
                              <circle cx="2" cy="8" r="2" fill="#6B7280" />
                              <circle cx="2" cy="14" r="2" fill="#6B7280" />
                            </svg>
                          </button>
                          {/* Desktop Dropdown menu */}
                          <Render when={invitationMenuOpen === invitation.uuid}>
                            <div className="absolute right-0 top-full mt-2 z-[100] w-[262px] bg-white rounded-[4px] shadow-[0px_0px_8px_0px_rgba(0,0,0,0.14)] border border-borderGrey">
                              <button
                                onClick={() => {
                                  setResendEmail(invitation.email);
                                  setShowResendModal(true);
                                  setInvitationMenuOpen(null);
                                }}
                                className="w-full flex items-center gap-3 py-4 px-4 font-poppins text-[14px] text-darkBlack hover:bg-gray-50 transition-colors rounded-[4px]"
                              >
                                <svg
                                  width="20"
                                  height="20"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13"
                                    stroke="#374151"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                                Resend invite
                              </button>
                            </div>
                          </Render>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </Render>
            </Render>

            {/* Invite User Modal - Desktop */}
            <Render when={showInviteModal && !isMobile}>
              <ModalComponent
                onClose={() => {
                  setShowInviteModal(false);
                  setInviteEmail('');
                }}
                containerClass="!w-[520px] !h-auto !p-0 !rounded-[8px] !overflow-hidden"
              >
                <div className="p-[24px] pt-[16px] flex flex-col gap-[24px]">
                  {/* Header */}
                  <div className="flex flex-col">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="font-poppins font-semibold text-[18px] leading-[100%] text-darkBlue">
                        Invite User
                      </h2>
                      <button
                        onClick={() => {
                          setShowInviteModal(false);
                          setInviteEmail('');
                        }}
                        className="hover:opacity-70 transition-opacity"
                      >
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M18 6L6 18M6 6L18 18"
                            stroke="#111653"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                    <p className="font-poppins font-normal text-[14px] leading-[100%] text-grayMedium">
                      Please enter the email address of the user you would like to invite.
                    </p>
                  </div>

                  {/* Email Input */}
                  <div className="flex flex-col gap-2">
                    <label className="font-poppins font-normal text-[12px] leading-[100%] text-[#49444E]">
                      Email
                    </label>
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder=""
                      className="w-full h-[40px] rounded-[4px] font-poppins font-normal text-[14px] leading-[100%] focus:outline-none focus:ring-1 focus:ring-primary px-4 py-[10px] border border-black/10 text-darkBlue"
                    />
                  </div>

                  {/* Button */}
                  <div className="flex justify-end">
                    <button
                      onClick={handleInviteUser}
                      disabled={!inviteEmail.trim() || isInviting}
                      className="h-[33px] rounded-[40px] font-poppins font-medium text-[14px] leading-[100%] text-white text-center disabled:opacity-50 transition-colors hover:opacity-90 px-5 py-1.5 bg-royalBlue"
                    >
                      {isInviting ? 'Sending...' : 'Send Invite'}
                    </button>
                  </div>
                </div>
              </ModalComponent>
            </Render>

            {/* Invite User Modal - Mobile Bottom Sheet */}
            <Render when={showInviteModal && isMobile}>
              {/* Backdrop */}
              <div
                className="fixed inset-0 bg-black bg-opacity-50 z-[9998]"
                onClick={() => {
                  setShowInviteModal(false);
                  setInviteEmail('');
                }}
              />
              {/* Modal Content */}
              <div className="fixed bottom-0 left-0 right-0 z-[9999] bg-white rounded-t-[16px] py-6 px-[18px]">
                <div className="flex flex-col gap-5">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <h2 className="font-poppins font-medium text-[18px] leading-[100%] text-darkBlack">
                      Invite User
                    </h2>
                    <button
                      onClick={() => {
                        setShowInviteModal(false);
                        setInviteEmail('');
                      }}
                      className="hover:opacity-70 transition-opacity"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 4L4 12M4 4L12 12"
                          stroke="#0B1215"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>

                  {/* Description */}
                  <p className="font-poppins font-normal text-[14px] leading-[100%] text-grayMedium">
                    Please enter the email address of the user you would like to invite.
                  </p>

                  {/* Email Input */}
                  <div className="flex flex-col gap-2">
                    <label className="font-poppins font-normal text-[12px] leading-[100%] text-black80">
                      Email
                    </label>
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder=""
                      className="w-full h-10 px-4 py-[10px] rounded-[4px] border border-black/10 bg-white text-darkBlack font-poppins font-normal text-[14px] leading-[100%] focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setShowInviteModal(false);
                        setInviteEmail('');
                      }}
                      className="w-[130px] h-12 px-2 py-4 rounded-[4px] border border-royalBlue bg-white text-royalBlue font-poppins font-medium text-[14px] leading-[100%] hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleInviteUser}
                      disabled={!inviteEmail.trim() || isInviting}
                      className="flex-1 h-12 px-2 py-4 rounded-[4px] border border-royalBlue bg-royalBlue text-white font-poppins font-medium text-[14px] leading-[100%] disabled:opacity-50 transition-colors hover:opacity-90"
                    >
                      {isInviting ? 'Sending...' : 'Send Invite'}
                    </button>
                  </div>
                </div>
              </div>
            </Render>

            {/* Resend Invite Confirmation Modal - Desktop */}
            <Render when={showResendModal && !isMobile}>
              <ModalComponent
                onClose={() => {
                  setShowResendModal(false);
                  setResendEmail('');
                }}
                containerClass="!w-[400px] !h-auto !p-0 !rounded-[8px] !overflow-hidden"
              >
                <div className="p-6 flex flex-col items-center gap-4">
                  {/* Paper Plane Icon */}
                  <div className="w-[64px] h-[64px] rounded-full bg-paleGreyTwo flex items-center justify-center">
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13"
                        stroke="#0102CE"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>

                  {/* Title */}
                  <h2 className="font-poppins font-semibold text-[18px] leading-[100%] text-center text-darkBlue">
                    Resend invite?
                  </h2>

                  {/* Description */}
                  <p className="font-poppins font-normal text-[14px] leading-[140%] text-center text-grayMedium">
                    Do you want to resend the invitation to this user?
                  </p>

                  {/* Buttons */}
                  <div className="flex gap-3 w-full mt-2">
                    <button
                      onClick={() => {
                        setShowResendModal(false);
                        setResendEmail('');
                      }}
                      className="flex-1 h-[44px] rounded-[4px] font-poppins font-medium text-[14px] leading-[100%] text-royalBlue border border-royalBlue bg-white hover:bg-gray-50 transition-colors"
                    >
                      Go back
                    </button>
                    <button
                      onClick={() => {
                        handleResendInvitation(resendEmail);
                        setShowResendModal(false);
                        setResendEmail('');
                      }}
                      disabled={isInviting}
                      className="flex-1 h-[44px] rounded-[4px] font-poppins font-medium text-[14px] leading-[100%] text-white bg-royalBlue disabled:opacity-50 transition-colors hover:opacity-90"
                    >
                      {isInviting ? 'Resending...' : 'Resend'}
                    </button>
                  </div>
                </div>
              </ModalComponent>
            </Render>

            {/* Resend Invite Confirmation Modal - Mobile Bottom Sheet */}
            <Render when={showResendModal && isMobile}>
              {/* Backdrop */}
              <div
                className="fixed inset-0 bg-black bg-opacity-50 z-[9998]"
                onClick={() => {
                  setShowResendModal(false);
                  setResendEmail('');
                }}
              />
              {/* Modal Content */}
              <div className="fixed bottom-0 left-0 right-0 z-[9999] bg-white rounded-t-2xl px-[18px] py-6">
                <div className="flex flex-col items-center gap-4">
                  {/* Paper Plane Icon */}
                  <div className="w-[64px] h-[64px] rounded-full bg-paleGreyTwo flex items-center justify-center">
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13"
                        stroke="#0102CE"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>

                  {/* Title */}
                  <h2 className="font-poppins font-medium text-[18px] leading-[100%] text-center text-darkBlack">
                    Resend invite?
                  </h2>

                  {/* Description */}
                  <p className="font-poppins font-normal text-[14px] leading-[100%] text-center text-grayMedium">
                    Do you want to resend the invitation to this user?
                  </p>

                  {/* Buttons */}
                  <div className="flex gap-3 w-full mt-2">
                    <button
                      onClick={() => {
                        setShowResendModal(false);
                        setResendEmail('');
                      }}
                      className="w-[130px] h-12 px-2 py-4 rounded-[4px] border border-royalBlue bg-white text-royalBlue font-poppins font-medium text-[14px] leading-[100%] hover:bg-gray-50 transition-colors"
                    >
                      Go back
                    </button>
                    <button
                      onClick={() => {
                        handleResendInvitation(resendEmail);
                        setShowResendModal(false);
                        setResendEmail('');
                      }}
                      disabled={isInviting}
                      className="flex-1 h-12 px-2 py-4 rounded-[4px] border border-royalBlue bg-royalBlue text-white font-poppins font-medium text-[14px] leading-[100%] disabled:opacity-50 transition-colors hover:opacity-90"
                    >
                      {isInviting ? 'Resending...' : 'Resend'}
                    </button>
                  </div>
                </div>
              </div>
            </Render>
          </div>
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white h-full flex items-center justify-center">
        <Loader size={8} />
      </div>
    );
  }

  return (
    <>
      {/* Desktop Layout - Using absolute positioning for guaranteed fixed headers */}
      <div className="hidden md:block bg-offWhite fixed top-0 right-0 bottom-0 left-[250px] overflow-hidden">
        {/* Business Details header - ABSOLUTE positioned, will NEVER move */}
        <div className="absolute top-0 left-0 right-0 h-[88px] z-10 px-8 flex items-center bg-white border-b border-borderGrey">
          <h1 className="font-poppins font-medium text-[24px] leading-[100%] tracking-[0.02em] text-darkBlue">
            Business Details
          </h1>
        </div>

        {/* Content area - ABSOLUTE positioned below header */}
        <div className="absolute top-[88px] left-0 right-0 bottom-0 pt-4 px-8 pb-8 overflow-hidden">
          <div className="w-full max-w-[1134px] h-full relative overflow-hidden border border-gray200 rounded-[4px] bg-white">
            {/* Header - ABSOLUTE positioned */}
            <div className="absolute top-0 left-0 right-0 h-[52px] z-[5] flex items-center justify-between px-5 py-3 border-b border-gray200 bg-white">
              <h2 className="font-poppins font-semibold text-[14px] leading-[100%] tracking-normal text-darkBlack">
                {activeTab === 'profile-info' ? 'Profile Details' : 'Update Business Details'}
              </h2>
              <Render when={activeTab === 'profile-info'}>
                {profileEditMode ? (
                  <Button
                    label={isUpdatingProfile ? 'Saving...' : 'Save Changes'}
                    onClick={handleProfileSave}
                    disabled={isUpdatingProfile}
                    loading={isUpdatingProfile}
                    customClass="!w-auto !h-[33px] !py-[6px] !px-[20px] !mb-0 !rounded-[40px]"
                  />
                ) : (
                  <button
                    onClick={() => setProfileEditMode(true)}
                    className="h-[33px] px-5 py-[6px] rounded-[40px] bg-royalBlue text-white font-poppins font-medium text-[14px] leading-[100%] hover:opacity-90 transition-opacity"
                  >
                    Edit
                  </button>
                )}
              </Render>
              <Render when={activeTab === 'additional-info' || activeTab === 'business-photos'}>
                <Button
                  label={isUpdating ? 'Saving...' : 'Save'}
                  onClick={handleButtonClick}
                  disabled={isUpdating}
                  loading={isUpdating}
                  customClass="!w-auto !h-[33px] !py-[6px] !px-[20px] !mb-0 !rounded-[40px]"
                />
              </Render>
              <Render when={activeTab === 'user-management'}>
                <button
                  onClick={() => setShowInviteModal(true)}
                  className="h-[36px] py-2 px-5 rounded-[40px] font-poppins font-medium text-[14px] text-white flex items-center gap-2 hover:opacity-90 transition-opacity bg-royalBlue"
                >
                  + Invite User
                </button>
              </Render>
            </div>

            {/* Tabs and Content row - positioned below header */}
            <div className="absolute top-[52px] left-0 right-0 bottom-0 overflow-hidden">
              {/* Left Tabs - ABSOLUTE positioned, will NEVER scroll */}
              <div className="absolute top-0 left-0 bottom-0 w-[280px] overflow-hidden border-r border-gray200 bg-white">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => handleTabSelect(tab.id)}
                    className={`w-full text-left px-5 py-4 font-poppins font-medium text-[12px] leading-[100%] tracking-normal flex items-center justify-between transition-colors ${
                      activeTab === tab.id
                        ? 'text-royalBlue bg-royalBlue/5'
                        : 'text-darkBlack hover:bg-gray-50'
                    }`}
                  >
                    {tab.label}
                    <svg
                      width="5"
                      height="8"
                      viewBox="0 0 5 8"
                      fill="none"
                      className={`relative top-[2px] left-[4px] ${activeTab === tab.id ? 'text-royalBlue' : 'text-gray-400'}`}
                    >
                      <path
                        d="M1 1L4 4L1 7"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                ))}
              </div>

              {/* Tab Content - ABSOLUTE positioned, ONLY THIS SCROLLS */}
              <div
                id="business-details-content"
                className="absolute top-0 left-[280px] right-0 bottom-0 overflow-y-auto overflow-x-hidden overscroll-contain"
              >
                {activeTab ? (
                  renderTabContent()
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    Select a tab to view details
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="md:hidden">
        <Render
          when={activeTab === null}
          elseRender={
            // Mobile Tab Content View
            <div className="min-h-screen">
              {/* Mobile Header with Back Button - Fixed below main header */}
              <div className="fixed top-[60px] left-0 right-0 z-40 bg-white flex items-center justify-between px-4 py-4 border-b border-gray200">
                <div className="flex items-center gap-3">
                  <button onClick={handleMobileBack} className="text-darkBlack hover:text-gray-700">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>
                  <h2 className="font-poppins font-medium text-[16px] text-darkBlack">
                    {activeTab === 'profile-info' ? 'Edit Profile' : getTabLabel(activeTab)}
                  </h2>
                </div>
              </div>

              {/* Mobile Tab Content - with padding top for fixed header */}
              <div className="pt-[60px] pb-[120px]">{renderTabContent()}</div>

              {/* Mobile Profile Save Button */}
              <Render when={activeTab === 'profile-info'}>
                <div className="fixed bottom-0 left-0 right-0 h-[98px] p-5 bg-white z-40 flex items-center border-t border-borderGrey">
                  <button
                    onClick={handleProfileSave}
                    disabled={isUpdatingProfile}
                    className="w-full h-[44px] rounded-[4px] font-poppins font-medium text-[14px] text-white bg-royalBlue px-5 py-3 transition-opacity hover:opacity-90 disabled:opacity-50"
                  >
                    {isUpdatingProfile ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </Render>

              <Render when={activeTab === 'additional-info' || activeTab === 'business-photos'}>
                <div className="fixed bottom-0 left-0 right-0 h-[98px] p-5 bg-white z-40 flex items-center border-t border-borderGrey">
                  <button
                    onClick={handleButtonClick}
                    disabled={isUpdating}
                    className="w-full h-[44px] rounded-[4px] font-poppins font-medium text-[14px] text-white bg-royalBlue px-5 py-3 transition-opacity hover:opacity-90 disabled:opacity-50"
                  >
                    {isUpdating ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </Render>

              <Render when={activeTab === 'user-management'}>
                <div className="fixed bottom-0 left-0 right-0 h-[98px] p-5 bg-white z-40 flex items-center border-t border-borderGrey">
                  <button
                    onClick={() => setShowInviteModal(true)}
                    disabled={isInviting}
                    className="w-full h-[44px] rounded-[4px] font-poppins font-medium text-[14px] text-white bg-royalBlue px-5 py-3 transition-opacity hover:opacity-90 disabled:opacity-50"
                  >
                    + Invite User
                  </button>
                </div>
              </Render>
            </div>
          }
        >
          {/* Mobile Tab List View */}
          <div className="px-4 pt-4 pb-4">
            <h1 className="font-poppins font-medium text-[16px] leading-[100%] text-darkBlue mb-4">
              Update Business Details
            </h1>
          </div>

          <div className="bg-white">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabSelect(tab.id)}
                className="w-full h-[58px] text-left p-5 font-poppins font-normal text-[12px] leading-[100%] flex items-center justify-between transition-colors text-darkBlack hover:bg-gray-50 border-b border-paleGreyTwo"
              >
                {tab.label}
                <svg
                  className="w-3 h-3 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            ))}
          </div>
        </Render>
      </div>
    </>
  );
};

export default BusinessDetails;
